import { useState, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from "react";
import { useListProducts, useCreateProduct, useUpdateProduct, useDeleteProduct, useListCategories, useCreateCategory, useUpdateCategory, useDeleteCategory, useUploadImage, customFetch } from "@workspace/api-client-react";
import type { Product, Category } from "@workspace/api-client-react/src/generated/api.schemas";
import { formatCurrency, parsePorcoes, pesoLabel } from "@/lib/utils";
import { hasPricedOptions, computeStartingFromPrice, NOME_DO_TURNO, TURNOS, faixaDoTurnoTexto, horarioDosTurnos, modoDaEncomenda, turnosDoProduto, type ModoEncomenda, type OptionGroup, type Turno } from "@workspace/pricing";
import { EmptyState } from "@/components/ui/empty-state";
import { FalhaAoCarregar } from "@/components/admin/FalhaAoCarregar";
import { errorTitle, TRY_AGAIN_DESCRIPTION } from "@/lib/messages";
import { aPartirDeComPromocao, getActivePromotion, getActiveQuantityPromo, quantityPromoLabel } from "@/lib/promotion";
import { useAuth } from "@/hooks/use-auth";
import { useIsUltra, useFeatureAllowed, useIsPaidPlan } from "@/hooks/use-plan";
import { UltraBadge } from "@/components/UltraBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataBr } from "@/components/ui/data-br";
import { ModalShell } from "@/components/ui/modal-shell";
import { BarcodeLookupField, type LookupResult } from "@/components/admin/BarcodeLookupField";
import { formatarCodigoProduto, limparCodigoProduto, unidadeDeMedida } from "@workspace/text";
import { Label } from "@/components/ui/label";
import { sentenceCasePtBR } from "@/lib/textNormalize";
import { Search, Plus, Edit2, Trash2, Image as ImageIcon, Loader2, AlertTriangle, ChevronDown, ChevronUp, Users, X, Pencil, ArrowUpDown, Copy, ExternalLink, Package, Sparkles, UtensilsCrossed, CalendarDays, CalendarClock, GripVertical, Clock, Tag, Folder, ListChecks, MinusCircle, BadgePercent, CircleDot, CheckSquare, Weight, Download, Filter, MoreVertical, Leaf, Pizza, SlidersHorizontal } from "lucide-react";
import type { ProductAvailabilityConfig, ProductAvailabilityWindow, ProductAvailabilityConfigMode } from "@workspace/api-client-react";
import { DAY_OF_WEEK_LABELS, DAY_OF_WEEK_SHORT, isAvailabilityConfigActive, summarizeAvailabilityConfig, computeNextAvailableDates, formatAvailabilityDateLabel } from "@/lib/availability";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { PublicLinkCard } from "@/components/PublicLinkCard";
import { CurrencyInput } from "@/components/ui/currency-input";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getListProductsQueryKey, getListCategoriesQueryKey } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { ReorderDialog } from "@/components/ReorderDialog";
import { SortableList } from "@/components/SortableList";
import { reportError } from "@/components/ErrorBoundary";
import { extractApiErrorMessage } from "@/lib/api-error";
import { OptionGroupTemplatesPicker, type CopiedGroup } from "@/components/admin/OptionGroupTemplatesPicker";
import { IngredientsManagerDialog, useIngredients } from "@/components/admin/IngredientsManagerDialog";
import { IngredientSelectField } from "@/components/admin/IngredientSelectField";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { MiniaturaDoVideo, PainelDoVideo, useEnvioDeVideo, type EnvioDeVideo } from "@/components/admin/VideoDoProduto";
import { ehArquivoDeVideo, estadoDoVideo } from "@/lib/videoDoProduto";

const LOWERCASE_WORDS = [
  "a", "as", "o", "os", "um", "uma", "uns", "umas",
  "de", "da", "das", "do", "dos", "em", "no", "na", "nos", "nas",
  "num", "numa", "nuns", "numas",
  "ao", "aos", "à", "às",
  "por", "pel", "pelo", "pela", "pelos", "pelas",
  "com", "sem", "sob", "sobre",
  "para", "ante", "após", "até", "contra", "desde", "entre", "perante", "tras",
  "dum", "duma", "duns", "dumas",
  "e", "ou", "mas", "nem", "que", "se", "como"
];

function buildAvailabilityConfig(input: {
  isOrderBased: boolean;
  enabled: boolean;
  mode: ProductAvailabilityConfigMode;
  weeklyDays: Record<number, ProductAvailabilityWindow[]>;
  monthlyDays: number[];
  monthlyRanges: ProductAvailabilityWindow[];
  specificDates: string[];
  specificRanges: ProductAvailabilityWindow[];
}): ProductAvailabilityConfig | null {
  if (!input.isOrderBased || !input.enabled) return null;
  const cleanRanges = (rs: ProductAvailabilityWindow[]) =>
    rs.filter(r => r.start && r.end && r.start < r.end).map(r => ({ start: r.start, end: r.end }));
  if (input.mode === "weekly") {
    const weekly = Object.entries(input.weeklyDays)
      .map(([dow, ranges]) => ({ dayOfWeek: Number(dow), ranges: cleanRanges(ranges) }))
      .filter(d => d.ranges.length > 0)
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek);
    if (!weekly.length) return null;
    return { enabled: true, mode: "weekly", weekly };
  }
  if (input.mode === "monthly") {
    const days = Array.from(new Set(input.monthlyDays.filter(d => d >= 1 && d <= 31))).sort((a, b) => a - b);
    const ranges = cleanRanges(input.monthlyRanges);
    if (!days.length || !ranges.length) return null;
    return { enabled: true, mode: "monthly", monthly: { days, ranges } };
  }
  if (input.mode === "specific_dates") {
    const dates = Array.from(new Set(input.specificDates)).sort();
    const ranges = cleanRanges(input.specificRanges);
    if (!dates.length || !ranges.length) return null;
    return { enabled: true, mode: "specific_dates", specificDates: { dates, ranges } };
  }
  return null;
}

// O horário de venda como o servidor grava: só os dias com faixa, em ordem; nenhum = null (sem limite).
function horarioDeVendaParaSalvar(dias: Record<number, ProductAvailabilityWindow[]>): { weekly: { dayOfWeek: number; ranges: ProductAvailabilityWindow[] }[] } | null {
  const weekly = Object.entries(dias)
    .map(([dow, rs]) => ({ dayOfWeek: Number(dow), ranges: rs.filter((r) => r.start && r.end && r.start < r.end).map((r) => ({ start: r.start, end: r.end })) }))
    .filter((d) => d.ranges.length > 0)
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek);
  return weekly.length ? { weekly } : null;
}
// A faixa não passa da meia-noite (regra do Pedro de 23/10): o fim depois do começo, no mesmo dia.
function erroDoHorarioDeVenda(dias: Record<number, ProductAvailabilityWindow[]>): string | null {
  const faixas = Object.values(dias).flat();
  if (faixas.length === 0) return "Marque pelo menos um dia com horário.";
  if (faixas.some((r) => !r.start || !r.end || r.start >= r.end))
    return "O fim tem que ser depois do começo, no mesmo dia. Para terça 16h até quarta 4h, marque terça das 16:00 às 23:59 e quarta das 00:00 às 04:00.";
  return null;
}

function capitalizeWords(text: string): string {
  return text
    .split(" ")
    .map((word, i, palavras) => {
      if (!word) return word;
      // "500 ml", "1 kg", "2L": a unidade depois do número fica como a loja digitou (o começo dela também, ao digitar).
      if (unidadeDeMedida(palavras, i, true)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function formatNameForSave(text: string): string {
  return text
    .split(" ")
    .map((word, index, palavras) => {
      if (!word) return word;
      if (unidadeDeMedida(palavras, index)) return word;
      const lower = word.toLowerCase();
      if (index > 0 && LOWERCASE_WORDS.includes(lower)) {
        return lower;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

const TEM_FIELD_SIZING = typeof CSS !== "undefined" && CSS.supports?.("field-sizing", "content");

// Descrição do produto em várias linhas que crescem com o texto (Pedro, 27/10/2026: o campo de uma linha escondia o
// texto e tirava as quebras de linha). Sem field-sizing (Chrome < 123), a altura acompanha o scrollHeight.
function CampoDescricao({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || TEM_FIELD_SIZING) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={3}
      className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-none [field-sizing:content]"
      data-testid="product-description"
    />
  );
}

export default function Products() {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const { user } = useAuth();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
  const catalogUrl = user?.store?.slug ? `${origin}${basePath}/loja/${user.store.slug}` : "";
  
  const { data: productsData, isLoading: loadingProducts, error: erroProdutos, refetch: recarregarProdutos, isFetching: buscandoProdutos } = useListProducts({ search: search || undefined, categoryId: selectedCategory !== "all" ? selectedCategory : undefined });
  const products = productsData ?? [];
  const { data: categoriesData, error: erroCategorias, refetch: recarregarCategorias, isFetching: buscandoCategorias } = useListCategories();
  const categories = categoriesData ?? [];
  // Falhou sem nada guardado: aviso de conexão, não «Nenhum produto cadastrado» (ver FalhaAoCarregar). Com a lista já
  // carregada, uma nova busca que falha mantém o que estava na tela.
  const falhouProdutos = !!erroProdutos && !productsData;
  const falhouCategorias = !!erroCategorias && !categoriesData;

  const [isProductModalOpen, setProductModalOpen] = useState(false);

  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [ingredientsManagerOpen, setIngredientsManagerOpen] = useState(false);
  const { data: ingredients = [] } = useIngredients();
  const [reorderOpen, setReorderOpen] = useState(false);
  const [filterOutOfStock, setFilterOutOfStock] = useState(false);

  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Deriving stats
  const kgZerado = (p: any) => p.soldByWeight === true && p.stockKg != null && parseFloat(String(p.stockKg)) <= 0;
  const kgComSaldo = (p: any) => p.soldByWeight === true && p.stockKg != null && parseFloat(String(p.stockKg)) > 0;
  const zerado = (p: any) => (p.stock !== null && p.stock === 0) || kgZerado(p);
  const outOfStock = products.filter(zerado).length;

  // Painel de filtros no padrão da página de Pedidos. Estoque NÃO tem filtro
  // de período de propósito: products.stock é um contador vivo — o banco não
  // guarda "quanto era o saldo em tal dia", então a planilha é sempre a foto
  // de agora.
  const [showFilters, setShowFilters] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSaldo, setFilterSaldo] = useState<"" | "com" | "sem">("");
  const hasFilters = !!filterStatus || !!filterSaldo || selectedCategory !== "all";

  // Exporta respeitando busca + painel de filtros, padrão dos outros exports.
  const [exporting, setExporting] = useState(false);
  const handleExportStock = async (format: "xlsx" | "csv" = "xlsx") => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (selectedCategory !== "all") params.set("categoryId", selectedCategory);
      if (filterStatus) params.set("status", filterStatus);
      const saldoEfetivo = filterSaldo || (filterOutOfStock ? "sem" : "");
      if (saldoEfetivo) params.set("saldo", saldoEfetivo);
      if (format === "csv") params.set("format", "csv");
      const qs = params.toString();
      const blob = await customFetch<Blob>(`/api/products/export${qs ? `?${qs}` : ""}`, {
        responseType: "blob",
      } as any);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "csv" ? "estoque.csv" : "estoque.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: "Não foi possível exportar o estoque",
        description: "Tente novamente em instantes.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  const displayedProducts = products.filter(p => {
    if (filterOutOfStock && !zerado(p)) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    if (filterSaldo === "com" && !((p.stock !== null && p.stock > 0) || kgComSaldo(p))) return false;
    if (filterSaldo === "sem" && !zerado(p)) return false;
    return true;
  });

  const showCategoryHeaders = selectedCategory === "all" && categories.length > 0 && !search && !filterOutOfStock;

  const groupedProducts = (() => {
    if (!showCategoryHeaders) return null;
    const groups: { categoryName: string; categoryId: string | null; products: typeof displayedProducts }[] = [];
    for (const cat of categories) {
      const catProducts = displayedProducts.filter(p => p.categoryId === cat.id);
      if (catProducts.length > 0) {
        groups.push({ categoryName: cat.name, categoryId: cat.id, products: catProducts });
      }
    }
    const uncategorized = displayedProducts.filter(p => !p.categoryId || !categories.find(c => c.id === p.categoryId));
    if (uncategorized.length > 0) {
      // "Outros" casa com a pílula e com o link público.
      groups.push({ categoryName: "Outros", categoryId: null, products: uncategorized });
    }
    return groups;
  })();

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Produtos</h1>
          <p className="text-muted-foreground mt-1">Gerencie sua loja, estoque e preços.</p>
        </div>
      </div>

      {outOfStock > 0 && (
        <Card 
          className={`p-4 flex items-center gap-4 cursor-pointer transition-all hover:shadow-md ${filterOutOfStock ? 'ring-2 ring-destructive bg-red-50 dark:bg-red-950/20' : ''}`}
          onClick={() => setFilterOutOfStock(f => !f)}
        >
          <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center"><AlertTriangle className="w-6 h-6"/></div>
          <div>
            <p className="text-2xl font-bold">{outOfStock}</p>
            <p className={`text-sm ${filterOutOfStock ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}>
              {filterOutOfStock ? 'Filtro ativo' : 'Sem Estoque'}
            </p>
          </div>
        </Card>
      )}

      {catalogUrl && (
        <PublicLinkCard
          url={catalogUrl}
          label="Link da Loja:"
          icon={<Package className="w-4 h-4 text-secondary" />}
          shareLabel="nossa loja digital"
        />
      )}

      {/* flex-wrap: em 360px com 3 botões (Ordenar/Ingredientes/Produto) os
          rótulos não cabem em uma linha — quebram pra segunda em vez de
          vazar pra fora. min-w-0 nos secundários impede que texto longo
          force overflow horizontal antes do wrap. */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setReorderOpen(true)} className="flex-1 min-w-0">
          <ArrowUpDown className="w-4 h-4 mr-2" />
          Ordenar
        </Button>
        {ingredients.length > 0 && (
          <Button variant="outline" onClick={() => setIngredientsManagerOpen(true)} className="flex-1 min-w-0">
            Ingredientes
          </Button>
        )}
        <Button onClick={() => { setEditingProduct(null); setProductModalOpen(true); }} className="flex-1 min-w-0 shadow-md shadow-primary/20">
          <Plus className="w-4 h-4 mr-2" />
          Produto
        </Button>
      </div>

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border/50 bg-muted/10 flex flex-col gap-3">
          <div className="flex gap-2 w-full">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Buscar por nome ou código"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-background pointer-coarse:min-h-11"
              />
            </div>
            <Button
              variant={hasFilters ? "default" : "outline"}
              onClick={() => setShowFilters(v => !v)}
              className="shrink-0"
              data-testid="products-filters-toggle"
            >
              <Filter className="w-4 h-4 mr-2" /> Filtros
              {hasFilters && <span className="ml-1 bg-white/20 text-xs px-1.5 rounded-full">!</span>}
            </Button>
          </div>
          {/* Pílulas no MESMO desenho do link público (rounded-full, cheia
              quando ativa), incluindo "Outros" pros produtos sem categoria. */}
          <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
            <button
              type="button"
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${selectedCategory === "all" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
              onClick={() => setSelectedCategory("all")}
            >
              Todas
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                type="button"
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${selectedCategory === cat.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                {sentenceCasePtBR(cat.name)}
              </button>
            ))}
            {categories.length > 0 && (
              <button
                type="button"
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${selectedCategory === "__others__" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                onClick={() => setSelectedCategory("__others__")}
                data-testid="products-cat-others"
              >
                Outros
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="p-4 border-b border-border/50 bg-muted/10 animate-in slide-in-from-top-2 duration-200">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1.5 flex-1 min-w-[140px]">
                <Label className="text-xs">Status</Label>
                <select className="w-full border rounded-md p-2 text-sm bg-background h-10" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="products-filter-status">
                  <option value="">Todos</option>
                  <option value="ativo">Ativo</option>
                  <option value="indisponivel">Indisponível</option>
                </select>
              </div>
              <div className="space-y-1.5 flex-1 min-w-[140px]">
                <Label className="text-xs">Estoque</Label>
                <select className="w-full border rounded-md p-2 text-sm bg-background h-10" value={filterSaldo} onChange={(e) => setFilterSaldo(e.target.value as any)} data-testid="products-filter-saldo">
                  <option value="">Todos</option>
                  <option value="com">Com saldo</option>
                  <option value="sem">Sem saldo</option>
                </select>
              </div>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={() => { setFilterStatus(""); setFilterSaldo(""); setSelectedCategory("all"); }}>
                  <X className="w-4 h-4 mr-1" /> Limpar
                </Button>
              )}
              <div className="grid grid-cols-2 gap-2 w-full">
                <Button variant="outline" onClick={() => handleExportStock("xlsx")} disabled={exporting} data-testid="products-export-stock">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Exportar Excel
                </Button>
                <Button variant="outline" onClick={() => handleExportStock("csv")} disabled={exporting} data-testid="products-export-stock-csv">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Exportar CSV
                </Button>
              </div>
            </div>
          </div>
        )}

        {falhouCategorias && !falhouProdutos && !loadingProducts && (
          <div className="p-4 pb-0">
            <FalhaAoCarregar tela="Produtos (categorias)" erro={erroCategorias} onTentar={() => recarregarCategorias()} tentando={buscandoCategorias} />
          </div>
        )}
        <div className="divide-y divide-border/50">
          {loadingProducts ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          ) : falhouProdutos ? (
            <div className="p-4">
              <FalhaAoCarregar
                tela="Produtos"
                erro={erroProdutos}
                onTentar={() => Promise.all([recarregarProdutos(), falhouCategorias ? recarregarCategorias() : null])}
                tentando={buscandoProdutos}
              />
            </div>
          ) : displayedProducts.length === 0 ? (
            filterOutOfStock ? (
              <EmptyState
                icon={AlertTriangle}
                title="Nenhum produto sem estoque"
                description="Todos os produtos têm estoque disponível no momento."
              />
            ) : (
              <EmptyState
                imageSrc={`${import.meta.env.BASE_URL}images/empty-products.png`}
                imageAlt="Sem produtos"
                title="Nenhum produto cadastrado"
                description="Adicione seu primeiro produto pra começar a vender. Você pode subir foto, escolher categoria e definir preço."
                action={
                  <Button onClick={() => { setEditingProduct(null); setProductModalOpen(true); }}>
                    <Plus className="w-4 h-4 mr-1" /> Adicionar primeiro produto
                  </Button>
                }
              />
            )
          ) : groupedProducts ? (
            groupedProducts.map((group, gi) => (
              <div key={group.categoryId ?? `__no_cat_${gi}`}>
                <div className="px-4 py-2 bg-muted/30 border-b border-border/50">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {sentenceCasePtBR(group.categoryName)}
                  </p>
                </div>
                <div className="divide-y divide-border/50">
                  {group.products.map(product => (
                    <ProductRow key={product.id} product={product} onEdit={(p) => { setEditingProduct(p); setProductModalOpen(true); }} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            displayedProducts.map(product => (
              <ProductRow key={product.id} product={product} onEdit={(p) => { setEditingProduct(p); setProductModalOpen(true); }} />
            ))
          )}
        </div>
      </Card>

      <ProductModal
        isOpen={isProductModalOpen}
        onClose={() => setProductModalOpen(false)}
        product={editingProduct}
        categories={categories}
        onOpenIngredientsManager={() => setIngredientsManagerOpen(true)}
        onEditProduct={async (id) => {
          // A lista pode estar filtrada (busca/categoria); se o duplicado não
          // estiver nela, busca por id.
          let p = (products as Product[]).find((x) => x.id === id);
          if (!p) {
            try { p = await customFetch<Product>(`/api/products/${id}`); } catch { p = undefined; }
          }
          if (p) setEditingProduct(p);
        }}
      />

      <IngredientsManagerDialog
        open={ingredientsManagerOpen}
        onClose={() => setIngredientsManagerOpen(false)}
      />

      <ReorderDialog
        open={reorderOpen}
        onOpenChange={setReorderOpen}
        tabs={[
          {
            key: "categories",
            label: "Categorias",
            items: categories.map((c) => ({ id: c.id, name: c.name })),
            onSave: async (items) => {
              await customFetch("/api/categories/reorder", {
                method: "PUT",
                body: JSON.stringify({ items }),
              });
              queryClient.invalidateQueries({ queryKey: getListCategoriesQueryKey() });
              queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
              toast({ title: "Ordem das categorias salva!" });
            },
          },
          {
            key: "products",
            label: "Produtos",
            sections: (() => {
              const sections: { label: string; items: { id: string; name: string }[] }[] = [];
              for (const cat of categories) {
                const catProducts = products.filter(p => p.categoryId === cat.id);
                if (catProducts.length > 0) {
                  sections.push({ label: cat.name, items: catProducts.map(p => ({ id: p.id, name: p.name })) });
                }
              }
              const uncategorized = products.filter(p => !p.categoryId || !categories.find(c => c.id === p.categoryId));
              if (uncategorized.length > 0) {
                sections.push({ label: "Outros", items: uncategorized.map(p => ({ id: p.id, name: p.name })) });
              }
              if (sections.length === 0) {
                sections.push({ label: "Produtos", items: products.map(p => ({ id: p.id, name: p.name })) });
              }
              return sections;
            })(),
            onSave: async (items) => {
              await customFetch("/api/products/reorder", {
                method: "PUT",
                body: JSON.stringify({ items }),
              });
              queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
              toast({ title: "Ordem dos produtos salva!" });
            },
          },
        ]}
      />
      
    </div>
  );
}

function ProductRow({ product, onEdit }: { product: any; onEdit: (p: any) => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [toggling, setToggling] = useState(false);
  const isActive = product.status !== "indisponivel";

  // Rotina diária de restaurante: ativar/desativar produto sem precisar abrir
  // o editor. PUT é partial (backend só aplica campos enviados), então mandamos
  // só status. Optimistic update no cache pra UI responder na hora.
  const toggleStatus = async (checked: boolean) => {
    const newStatus = checked ? "ativo" : "indisponivel";
    setToggling(true);
    // setQueriesData (prefix match) — useListProducts cacheia a lista com
    // filtros embutidos na queryKey (search, categoryId). setQueryData
    // exigiria key exata e não bate com a usada na página, deixando a UI
    // sem atualizar visualmente embora o PUT abaixo persista no banco.
    queryClient.setQueriesData({ queryKey: getListProductsQueryKey() }, (old: any) => {
      if (!Array.isArray(old)) return old;
      return old.map((p: any) => (p.id === product.id ? { ...p, status: newStatus } : p));
    });
    try {
      await customFetch(`/api/products/${product.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
    } catch {
      queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      toast({ title: errorTitle("atualizar o produto"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
    } finally {
      setToggling(false);
    }
  };

  // Vídeo pronto como 1º item (ou sem foto): a miniatura é a capa escolhida, como no cardápio.
  const ev = estadoDoVideo(product.video);
  const capaDoVideo = ev?.status === "pronto" && ev.capas ? (ev.capas[ev.capa] ?? ev.capas[0]) : null;
  const miniatura = capaDoVideo && (ev!.posicao === 0 || !product.imageUrl) ? capaDoVideo : product.imageUrl;

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/10 transition-colors group">
      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden border border-border/50 shrink-0">
        {miniatura ? (
          <img src={miniatura} alt={product.name} className={`w-full h-full object-cover ${!isActive ? "opacity-40 grayscale" : ""}`} />
        ) : (
          <ImageIcon className="w-5 h-5 text-muted-foreground/50" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`font-semibold text-sm leading-tight line-clamp-2 md:line-clamp-none md:truncate ${isActive ? "text-foreground" : "text-muted-foreground"}`}>{product.name}</p>
          {!isActive && <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full shrink-0">Indisponível</span>}
          {(product as any).barcode && <span className="text-xs font-mono text-muted-foreground shrink-0" data-testid="product-code">{formatarCodigoProduto((product as any).barcode)}</span>}
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {/* Categoria + estoque num item só: quebravam em 2 linhas separadas
              e o badge descia quebrado — juntos, sobra a linha pro badge.
              Categoria longa encurta com "…" (min-w-0 + truncate): sem isso a
              linha passava da coluna e o estoque ficava por cima do preço no
              celular (Koma, "Tortas e cakes salgadas", 03/10/2026). */}
          <span className="flex items-center gap-2 whitespace-nowrap min-w-0 max-w-full">
          <p className="text-xs text-muted-foreground truncate min-w-0">{sentenceCasePtBR(product.category?.name || "") || "Outros"}</p>
          <span className="text-muted-foreground/40 text-xs shrink-0">·</span>
          <p className="text-xs text-muted-foreground shrink-0">
            <span className={((product.stock !== null && product.stock === 0) || ((product as any).soldByWeight && (product as any).stockKg != null && parseFloat(String((product as any).stockKg)) <= 0)) ? "text-destructive font-semibold" : ""}>{(product as any).soldByWeight
                ? ((product as any).stockKg == null ? "Sem controle" : `${String(parseFloat(String((product as any).stockKg))).replace(".", ",")} kg`)
                : product.stock === null ? "Sem controle" : `${product.stock} un`}</span>
          </p>
          </span>
          {(product as any).servings && (
            <>
              <span className="text-muted-foreground/40 text-xs">·</span>
              <p className="text-xs text-muted-foreground">Serve {(product as any).servings}</p>
            </>
          )}
          {(product as any).slices > 0 && (
            <>
              <span className="text-muted-foreground/40 text-xs">·</span>
              <p className="text-xs text-muted-foreground">{(product as any).slices} {(product as any).slices === 1 ? "fatia" : "fatias"}</p>
            </>
          )}
          {(product as any).isOrderBased && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              <CalendarClock className="w-3 h-3" />
              Por encomenda
            </span>
          )}
          {(product as any).isVegan && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              <Leaf className="w-3 h-3" />
              Vegano
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        <div className="flex items-center gap-2">
          <Switch
            checked={isActive}
            onCheckedChange={toggleStatus}
            disabled={toggling}
            aria-label={isActive ? "Desativar produto" : "Ativar produto"}
            title={isActive ? "Desativar produto" : "Ativar produto"}
          />
          {/* <md: Editar/Excluir no ⋮ — 3 controles inline truncavam o nome. */}
          <div className="hidden md:flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10" onClick={() => onEdit(product)}>
              <Edit2 className="w-4 h-4" />
            </Button>
            <DeleteProductButton id={product.id} />
          </div>
          <ProductRowMenu product={product} onEdit={onEdit} />
        </div>
        {(() => {
          // Promo por quantidade calculada ANTES dos ramos de preço: o produto
          // de base 0 ("A partir de...") retornava cedo e o selo nunca
          // aparecia (caso Blusa). Lista do lojista vê qualquer canal.
          const qtyLabel = quantityPromoLabel(
            getActiveQuantityPromo(product, new Date(), "link") ??
              getActiveQuantityPromo(product, new Date(), "pdv"),
          );
          const qtyBadge = qtyLabel ? (
            <span className="text-[9px] font-bold uppercase tracking-wider text-accent-foreground bg-accent px-1.5 py-0.5 rounded">Promo</span>
          ) : null;
          const qtyCaption = qtyLabel ? (
            <span className="text-[9px] text-accent font-medium text-right leading-tight max-w-[140px]">{qtyLabel}</span>
          ) : null;
          const base = Number((product as any).price);
          if (!Number.isFinite(base) || base <= 0) {
            // Com a promoção em % nas opções (Pedro, 23/10/2026), o «A partir de» com desconto e o original riscado.
            const ap = aPartirDeComPromocao(product);
            if (ap && ap.original != null) {
              return (
                <div className="flex flex-col items-end">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-accent-foreground bg-accent px-1.5 py-0.5 rounded">Promo</span>
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] font-normal text-muted-foreground">A partir de</span>
                    <span className="text-xs text-muted-foreground line-through">{formatCurrency(ap.original)}</span>
                    <span className="font-semibold text-accent text-sm">{formatCurrency(ap.valor)}</span>
                  </div>
                </div>
              );
            }
            const sf = computeStartingFromPrice((product as any).optionGroups, (product as any).startingPriceOverride);
            if (sf && sf > 0) {
              return (
                <div className="flex flex-col items-end gap-0.5">
                  {qtyBadge}
                  <p className="font-semibold text-foreground text-sm">
                    <span className="text-[11px] font-normal text-muted-foreground">A partir de </span>
                    {formatCurrency(sf)}
                  </p>
                  {qtyCaption}
                </div>
              );
            }
          }
          const promo = getActivePromotion(product);
          if (promo.active) {
            return (
              <div className="flex flex-col items-end">
                <span className="text-[9px] font-bold uppercase tracking-wider text-accent-foreground bg-accent px-1.5 py-0.5 rounded">Promo</span>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground line-through">{formatCurrency(product.price)}</span>
                  <span className="font-semibold text-accent text-sm">{formatCurrency(promo.finalPrice)}</span>
                </div>
              </div>
            );
          }
          if (qtyLabel) {
            return (
              <div className="flex flex-col items-end gap-0.5">
                {qtyBadge}
                <p className="font-semibold text-foreground text-sm">{formatCurrency(product.price)}</p>
                {qtyCaption}
              </div>
            );
          }
          return <p className="font-semibold text-foreground text-sm">{formatCurrency(product.price)}</p>;
        })()}
      </div>
    </div>
  );
}

// Fluxo de excluir compartilhado entre o botão inline (desktop) e o item do
// menu ⋮ (mobile) — um confirm, uma mutation.
function useDeleteProductFlow(id: string) {
  const { toast } = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteProduct({
    mutation: {
      onSuccess: () => {
        toast({ title: "Produto excluído" });
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
      },
      onError: () => toast({ title: errorTitle("excluir o produto"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" })
    }
  });
  const run = async () => {
    if (await confirm({ title: "Excluir este produto?", description: "O produto sai do catálogo e não dá pra desfazer.", confirmLabel: "Excluir" })) deleteMutation.mutate({ id });
  };
  return { run, pending: deleteMutation.isPending };
}

function ProductRowMenu({ product, onEdit }: { product: any; onEdit: (p: any) => void }) {
  const { run, pending } = useDeleteProductFlow(product.id);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="md:hidden h-9 w-9" aria-label={`Ações de ${product.name}`}>
          <MoreVertical className="w-5 h-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => onEdit(product)}>
          <Edit2 className="w-4 h-4 mr-2" /> Editar
        </DropdownMenuItem>
        <DropdownMenuItem className="text-destructive focus:text-destructive" disabled={pending} onClick={run}>
          <Trash2 className="w-4 h-4 mr-2" /> Excluir
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DeleteProductButton({ id }: { id: string }) {
  const { run, pending } = useDeleteProductFlow(id);
  return (
    <Button 
      variant="ghost" 
      size="icon" 
      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
      onClick={run}
      disabled={pending}
    >
      {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
    </Button>
  );
}

interface OptionItemForm {
  /** Identidade real da opção no jsonb (ver OptionGroupForm.gid). */
  oid?: string;
  // id sintético só do client — chave estável pro React e pro drag-and-drop
  // (SortableList). Não é persistido: o save mapeia campos explícitos.
  id: string;
  name: string;
  price: string;
  // Estoque da opção personalizada. "" = sem controle (null no jsonb).
  // Só editável quando optionType é "personalizado" — ingrediente usa o
  // estoque da tabela ingredients.
  stock: string;
  // Snapshot do estoque carregado ao abrir o modal. Vai junto no save: o
  // servidor só aplica `stock` se difere de `stockBefore` (lojista mexeu);
  // senão preserva o contador vivo do banco (pedidos debitam em paralelo).
  stockBefore: string;
  // Quando o grupo tem optionType "ingredientes", aponta pra ingredients.id.
  // Em modo "personalizado" fica vazio.
  ingredientId?: string;
  // Adicionais fracionados (Pedro, 15/10/2026; Profissional): quanto a opção conta no limite do grupo e no pacote —
  // ½, 1 ou 2; 1 não grava. Com o interruptor desligado vai como `countAsOff` (guardado, não conta).
  countAs?: number;
  // Pacote de escolhas: false = o adicional cobra o preço dele e não conta no pacote (29/10/2026). Ausente = conta.
  inPackage?: boolean;
}

interface OptionGroupForm {
  // id sintético só do client (ver OptionItemForm.id).
  id: string;
  // ⚠️ Identidade REAL do grupo no jsonb. O form só PRESERVA — quem gera é o
  // servidor. Trocar isso a cada salvamento invalidaria carrinho do cliente.
  gid?: string;
  name: string;
  selectionType: "single" | "multiple";
  maxSelections: string;
  minSelections: string;
  required: boolean;
  // Só em "multiple": cada opção no máx. 1 vez (storefront vira liga/desliga).
  onePerOption: boolean;
  priceMode: "sum" | "highest" | "average";
  optionType: "personalizado" | "ingredientes";
  options: OptionItemForm[];
  saveAsTemplate?: boolean;
  // Pacote de escolhas: false = esta lista não conta no pacote (segue com o próprio limite). Ausente = conta.
  inPackage?: boolean;
}

/**
 * Piso do grupo pronto pra gravar. ⚠️ ORIGEM ÚNICA do `minSelections`: ele só
 * existe em escolha múltipla COM "Obrigatório" ligado — é o toggle que decide
 * se há piso, e sem ele o campo nem aparece na tela. Sem essa trava o valor
 * sobrevivia no estado do form (grupo carregado do banco, modelo copiado) e
 * voltava a ser gravado escondido, barrando o cliente por uma regra que o
 * lojista não vê. Também clampa no teto: min 5 com máx 3 gravaria um grupo
 * impossível de fechar. `undefined` = a chave nem entra no JSON.
 */
function pisoDoGrupo(g: Pick<OptionGroupForm, "selectionType" | "required" | "minSelections" | "maxSelections">): number | undefined {
  if (g.selectionType !== "multiple" || !g.required) return undefined;
  const min = parseInt(g.minSelections, 10);
  if (!Number.isFinite(min) || min <= 1) return undefined;
  const teto = parseInt(g.maxSelections, 10);
  return Number.isFinite(teto) && teto > 0 ? Math.min(min, teto) : min;
}

// Contador monotônico pra ids sintéticos de grupos/opções (estáveis na sessão).
let optionUidSeq = 0;
const nextOptionUid = () => `opt-${++optionUidSeq}`;

const FOOD_PREF_KEY = "vnz-food-pref";

type DecodedImage = { source: CanvasImageSource; width: number; height: number; cleanup: () => void };

// O que aconteceu ao abrir a foto, para o aviso e para o relatório [image-normalize]: se o arquivo nem é imagem, e o
// que o navegador respondeu ao createImageBitmap e ao FileReader.
type LeituraDaFoto = { naoEImagem?: boolean; bitmap?: string; leitor?: string };

const motivoDoNavegador = (e: unknown): string =>
  (e && typeof e === "object" && "name" in e
    ? `${(e as { name: string }).name}: ${(e as { message?: string }).message ?? ""}`
    : String(e)).slice(0, 120);

async function decodeViaImageBitmap(file: File, leitura: LeituraDaFoto): Promise<DecodedImage | null> {
  if (typeof createImageBitmap !== "function") {
    leitura.bitmap = "sem createImageBitmap";
    return null;
  }
  try {
    const bmp = await createImageBitmap(file);
    return {
      source: bmp,
      width: bmp.width,
      height: bmp.height,
      cleanup: () => { try { (bmp as any).close?.(); } catch {} },
    };
  } catch (e) {
    leitura.bitmap = motivoDoNavegador(e);
    return null;
  }
}

async function loadImageElement(src: string): Promise<HTMLImageElement> {
  const img = new Image();
  img.src = src;
  if (typeof img.decode === "function") {
    try {
      await img.decode();
      return img;
    } catch {
      // cai no onload/onerror abaixo
    }
  }
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Falha ao decodificar imagem (img)"));
  });
  return img;
}

async function decodeViaImgTag(file: File): Promise<DecodedImage> {
  // NÃO usar crossOrigin em blob URL: alguns navegadores (MiuiBrowser/Xiaomi) tratam
  // blobs com "crossOrigin=anonymous" como cross-origin e falham silenciosamente.
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImageElement(objectUrl);
    return {
      source: img,
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      cleanup: () => URL.revokeObjectURL(objectUrl),
    };
  } catch (e) {
    URL.revokeObjectURL(objectUrl);
    throw e;
  }
}

// Fallback final: lê o arquivo como data URL e carrega num <img>.
// Mais lento e usa mais memória, mas funciona em browsers (ex.: MiuiBrowser
// no Xiaomi) que falham com blob URLs ou createImageBitmap.
async function decodeViaDataUrl(file: File, leitura: LeituraDaFoto): Promise<DecodedImage> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      if (typeof r === "string") resolve(r);
      else reject(new Error("FileReader retornou tipo inesperado"));
    };
    reader.onerror = () => {
      leitura.leitor = reader.error ? motivoDoNavegador(reader.error) : "sem detalhe";
      reject(new Error("Falha ao ler arquivo (FileReader)"));
    };
    reader.readAsDataURL(file);
  });
  const img = await loadImageElement(dataUrl);
  return {
    source: img,
    width: img.naturalWidth || img.width,
    height: img.naturalHeight || img.height,
    cleanup: () => {},
  };
}

function canvasLooksUniform(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
  try {
    const samples: Array<[number, number]> = [
      [Math.floor(w * 0.1), Math.floor(h * 0.1)],
      [Math.floor(w * 0.5), Math.floor(h * 0.5)],
      [Math.floor(w * 0.9), Math.floor(h * 0.9)],
      [Math.floor(w * 0.25), Math.floor(h * 0.75)],
      [Math.floor(w * 0.75), Math.floor(h * 0.25)],
    ];
    let first: [number, number, number, number] | null = null;
    for (const [x, y] of samples) {
      const data = ctx.getImageData(x, y, 1, 1).data;
      const px: [number, number, number, number] = [data[0], data[1], data[2], data[3]];
      if (!first) {
        first = px;
        continue;
      }
      if (Math.abs(px[0] - first[0]) > 3) return false;
      if (Math.abs(px[1] - first[1]) > 3) return false;
      if (Math.abs(px[2] - first[2]) > 3) return false;
      if (Math.abs(px[3] - first[3]) > 3) return false;
    }
    return true;
  } catch {
    return false;
  }
}

async function drawAndEncode(
  decoded: DecodedImage,
  maxDim: number,
  quality: number,
): Promise<{ blob: Blob; ext: string; mime: string; suspectEmpty: boolean; w: number; h: number }> {
  const { source, width: w0, height: h0 } = decoded;
  if (!w0 || !h0) throw new Error("Imagem sem dimensões");
  const scale = Math.min(1, maxDim / Math.max(w0, h0));
  const w = Math.max(1, Math.round(w0 * scale));
  const h = Math.max(1, Math.round(h0 * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D não suportado");
  try {
    ctx.drawImage(source, 0, 0, w, h);
  } catch (drawErr: any) {
    throw new Error(`Falha ao desenhar no canvas: ${drawErr?.message || drawErr}`);
  }
  let blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", quality));
  let ext = "webp";
  let mime = "image/webp";
  if (!blob || blob.size === 0) {
    blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    ext = "jpg";
    mime = "image/jpeg";
  }
  if (!blob || blob.size === 0) {
    throw new Error("Falha ao codificar imagem (blob vazio após webp+jpeg)");
  }
  const minSizeForArea = Math.max(800, Math.floor((w * h) / 400));
  const tooSmallForArea = blob.size < minSizeForArea;
  const bigEnoughToCheck = Math.max(w, h) > 32;
  const uniform = bigEnoughToCheck && canvasLooksUniform(ctx, w, h);
  const suspectEmpty = bigEnoughToCheck && uniform && tooSmallForArea;
  return { blob, ext, mime, suspectEmpty, w, h };
}

async function normalizeImageFile(file: File, leitura: LeituraDaFoto = {}, maxDim = 1600, quality = 0.85): Promise<File> {
  if (typeof window === "undefined") return file;
  const lowerName = (file.name || "").toLowerCase();
  const isHeic = /\.(heic|heif)$/.test(lowerName) || /heic|heif/.test(file.type || "");
  // HEIC/HEIF: o canvas do browser não decodifica esse formato. Em vez de
  // bloquear o lojista (típico em fotos de iPhone), enviamos o arquivo cru
  // para o servidor — o sharp/libheif converte para webp no upload.
  if (isHeic) {
    return file;
  }
  if (!(file.type || "").startsWith("image/") && !/\.(jpe?g|png|webp|gif|bmp)$/i.test(lowerName)) {
    leitura.naoEImagem = true;
    throw new Error(`Arquivo não parece ser uma imagem (tipo: ${file.type || "desconhecido"})`);
  }

  const bitmapDecoded = await decodeViaImageBitmap(file, leitura);
  let result: Awaited<ReturnType<typeof drawAndEncode>> | null = null;
  let fallbackPath: "none" | "img" | "data-url" = "none";

  if (bitmapDecoded) {
    try {
      result = await drawAndEncode(bitmapDecoded, maxDim, quality);
    } finally {
      bitmapDecoded.cleanup();
    }
  }

  if (!result || result.suspectEmpty) {
    let imgDecoded: DecodedImage | null = null;
    try {
      imgDecoded = await decodeViaImgTag(file);
    } catch {
      imgDecoded = null;
    }
    if (imgDecoded) {
      try {
        const fallback = await drawAndEncode(imgDecoded, maxDim, quality);
        if (!fallback.suspectEmpty) {
          result = fallback;
          fallbackPath = "img";
        }
      } catch {
        // segue para o fallback data URL
      } finally {
        imgDecoded.cleanup();
      }
    }
  }

  if (!result || result.suspectEmpty) {
    // Último recurso: data URL via FileReader. Cobre browsers
    // (ex.: MiuiBrowser/Xiaomi) onde blob URL e createImageBitmap falham.
    const dataDecoded = await decodeViaDataUrl(file, leitura);
    try {
      const fallback = await drawAndEncode(dataDecoded, maxDim, quality);
      if (fallback.suspectEmpty) {
        throw new Error("Imagem decodificada como vazia (cinza). Tente reenviar a foto a partir da galeria do celular.");
      }
      result = fallback;
      fallbackPath = "data-url";
    } finally {
      dataDecoded.cleanup();
    }
  }

  if (fallbackPath !== "none") {
    try { console.error(`[image-normalize] usado fallback ${fallbackPath} após createImageBitmap`); } catch {}
  }

  const baseName = (file.name || "imagem").replace(/\.[^.]+$/, "") || "imagem";
  return new File([result.blob], `${baseName}.${result.ext}`, { type: result.mime });
}

const MAX_PRODUCT_IMAGES = 5;

// Normaliza + envia cada arquivo, devolvendo as URLs. Compartilhado entre o
// quadrado de imagem do form e o PhotoManagerModal.
async function uploadProductImageFiles(
  files: File[],
  remaining: number,
  uploadMutation: { mutateAsync: (v: { data: { file: File } }) => Promise<{ url: string }> },
  toast: (opts: { title: string; description?: string; variant?: "destructive" }) => void,
): Promise<string[]> {
  if (files.length === 0) return [];
  if (remaining <= 0) {
    toast({
      title: "Limite atingido",
      description: `O produto pode ter no máximo ${MAX_PRODUCT_IMAGES} fotos.`,
      variant: "destructive",
    });
    return [];
  }
  const picked = files.slice(0, remaining);
  const uploaded: string[] = [];
  for (const raw of picked) {
    if (raw.size === 0) {
      toast({
        title: "Imagem vazia",
        description: `"${raw.name}" está com 0 bytes. Verifique se o arquivo baixou corretamente e tente de novo.`,
        variant: "destructive",
      });
      continue;
    }
    let normalized: File;
    const leitura: LeituraDaFoto = {};
    try {
      normalized = await normalizeImageFile(raw, leitura);
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      const doNavegador = `${leitura.leitor ? ` | leitor=${leitura.leitor}` : ""}${leitura.bitmap ? ` | bitmap=${leitura.bitmap}` : ""}`;
      const wrapped = new Error(`[image-normalize] ${reason}${doNavegador} | name=${raw.name} type=${raw.type || "?"} size=${raw.size}`);
      reportError(wrapped, null);
      // Formato, só quando o arquivo nem é imagem. Nos outros casos é uma foto que o navegador não conseguiu abrir (quase
      // sempre o Chrome Android sem conseguir ler os bytes, de JPG e de PNG): pedir outro formato engana o lojista.
      toast(leitura.naoEImagem
        ? {
            title: "Imagem não suportada",
            description: `Não conseguimos ler "${raw.name}". Tente outra no formato JPG ou PNG.`,
            variant: "destructive",
          }
        : {
            title: "Não conseguimos abrir essa foto",
            description: `O celular não deixou o sistema abrir "${raw.name}". Tente escolher a foto de novo ou tire um print dela e envie o print.`,
            variant: "destructive",
          });
      continue;
    }
    try {
      const res = await uploadMutation.mutateAsync({ data: { file: normalized } });
      uploaded.push(res.url);
    } catch (uploadErr: any) {
      const msg = extractApiErrorMessage(uploadErr, "Não foi possível enviar a imagem.");
      const wrapped = new Error(`[image-upload] ${msg} | status=${uploadErr?.status ?? "?"} name=${normalized.name} type=${normalized.type} size=${normalized.size}`);
      reportError(wrapped, null);
      toast({ title: errorTitle("enviar a imagem"), description: msg, variant: "destructive" });
    }
  }
  return uploaded;
}

// Fotos e o vídeo do produto, na ordem do lojista (o vídeo ocupa um dos 5 lugares).
type ItemDoEditor = { tipo: "foto"; url: string } | { tipo: "video"; id: string };

function PhotoManagerModal({
  isOpen,
  onClose,
  initialItens,
  onSave,
  envios,
  capaDoVideo,
  onEscolherVideo,
  onDescartarVideo,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialItens: ItemDoEditor[];
  onSave: (itens: ItemDoEditor[]) => void;
  envios: Record<string, EnvioDeVideo>;
  capaDoVideo: number;
  onEscolherVideo: (arquivo: File) => Promise<string | null>;
  onDescartarVideo: (id: string) => void;
}) {
  const [items, setItems] = useState<ItemDoEditor[]>(initialItens);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const uploadMutation = useUploadImage();
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setItems(initialItens);
      setDragIndex(null);
      setDragOverIndex(null);
    }
  }, [isOpen, initialItens]);

  const videoAplicado = initialItens.find((it) => it.tipo === "video")?.id ?? null;
  // Fechar sem aplicar desfaz o vídeo escolhido aqui (o envio dele para).
  const fechar = () => {
    const v = items.find((it): it is { tipo: "video"; id: string } => it.tipo === "video");
    if (v && v.id !== videoAplicado) onDescartarVideo(v.id);
    onClose();
  };

  const addFiles = async (files: File[]) => {
    setUploading(true);
    try {
      const videos = files.filter(ehArquivoDeVideo);
      const fotos = files.filter((f) => !ehArquivoDeVideo(f));
      let ocupados = items.length;
      if (videos.length > 0) {
        if (videos.length > 1) toast({ title: "Só 1 vídeo por produto", description: "Fica o primeiro que você escolheu." });
        const atual = items.find((it): it is { tipo: "video"; id: string } => it.tipo === "video");
        if (!atual && items.length >= MAX_PRODUCT_IMAGES) {
          toast({ title: "Limite atingido", description: `O produto pode ter no máximo ${MAX_PRODUCT_IMAGES} fotos, contando o vídeo. Tire uma foto para pôr o vídeo.`, variant: "destructive" });
        } else {
          const id = await onEscolherVideo(videos[0]!);
          if (id) {
            // Um vídeo só: o novo fica no lugar do anterior.
            setItems((prev) => {
              const i = prev.findIndex((it) => it.tipo === "video");
              if (i < 0) return [...prev, { tipo: "video" as const, id }];
              const next = [...prev];
              next[i] = { tipo: "video", id };
              return next;
            });
            if (atual && atual.id !== videoAplicado) onDescartarVideo(atual.id);
            if (!atual) ocupados++;
          }
        }
      }
      if (fotos.length > 0) {
        const uploaded = await uploadProductImageFiles(fotos, MAX_PRODUCT_IMAGES - ocupados, uploadMutation, toast);
        if (uploaded.length > 0) {
          setItems((prev) => [...prev, ...uploaded.map((url) => ({ tipo: "foto" as const, url }))].slice(0, MAX_PRODUCT_IMAGES));
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const moveUp = (i: number) => {
    if (i === 0) return;
    setItems((prev) => {
      const next = [...prev];
      [next[i - 1], next[i]] = [next[i], next[i - 1]];
      return next;
    });
  };

  const moveDown = (i: number) => {
    setItems((prev) => {
      if (i === prev.length - 1) return prev;
      const next = [...prev];
      [next[i + 1], next[i]] = [next[i], next[i + 1]];
      return next;
    });
  };

  return (
    <ModalShell
      open={isOpen}
      onOpenChange={(open) => { if (!open) fechar(); }}
      title="Fotos do produto"
      subtitle={`Arraste pra reordenar — a primeira é a principal. Até ${MAX_PRODUCT_IMAGES} fotos, e uma delas pode ser um vídeo de até 30 s.`}
      size="md"
      footer={
        <>
          <Button type="button" variant="outline" onClick={fechar} disabled={uploading}>Cancelar</Button>
          <Button
            type="button"
            onClick={() => { onSave(items); onClose(); }}
            disabled={uploading}
          >
            Aplicar
          </Button>
        </>
      }
    >
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {items.map((item, i) => {
              const isDragging = dragIndex === i;
              const isOver = dragOverIndex === i && dragIndex !== null && dragIndex !== i;
              const ehVideo = item.tipo === "video";
              return (
                <div
                  key={item.tipo === "foto" ? `${item.url}-${i}` : `video-${item.id}`}
                  data-testid={ehVideo ? "foto-item-video" : undefined}
                  draggable
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDrop={(e) => handleDrop(e, i)}
                  onDragEnd={handleDragEnd}
                  className={`relative aspect-square rounded-lg overflow-hidden border bg-muted group cursor-grab active:cursor-grabbing transition-all ${isDragging ? "opacity-40" : ""} ${isOver ? "ring-2 ring-primary border-primary" : "border-border"}`}
                >
                  {item.tipo === "foto" ? (
                    <img src={item.url} alt={`Foto ${i + 1}`} className="w-full h-full object-cover pointer-events-none" />
                  ) : (
                    <MiniaturaDoVideo envio={envios[item.id]} capa={item.id === videoAplicado ? capaDoVideo : 0} />
                  )}
                  {i === 0 && (
                    <span className="absolute top-1 left-1 text-[9px] font-bold tracking-wider bg-primary text-primary-foreground px-1.5 py-0.5 rounded shadow">
                      PRINCIPAL
                    </span>
                  )}
                  <span className="absolute bottom-1 left-1 text-[10px] font-semibold bg-black/60 text-white px-1.5 py-0.5 rounded">
                    {i + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeAt(i)}
                    aria-label={ehVideo ? "Remover vídeo" : `Remover foto ${i + 1}`}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-destructive text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 px-1 py-1 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity sm:flex hidden">
                    <button
                      type="button"
                      onClick={() => moveUp(i)}
                      disabled={i === 0}
                      aria-label="Mover para cima"
                      className="text-white text-[10px] px-1 py-0.5 rounded bg-white/20 disabled:opacity-30"
                    >
                      ←
                    </button>
                    <GripVertical className="w-3 h-3 text-white/70" />
                    <button
                      type="button"
                      onClick={() => moveDown(i)}
                      disabled={i === items.length - 1}
                      aria-label="Mover para baixo"
                      className="text-white text-[10px] px-1 py-0.5 rounded bg-white/20 disabled:opacity-30"
                    >
                      →
                    </button>
                  </div>
                </div>
              );
            })}
            {items.length < MAX_PRODUCT_IMAGES && (
              <label className={`relative overflow-hidden aspect-square rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:bg-muted/40 cursor-pointer transition-colors ${uploading ? "opacity-60 cursor-wait" : ""}`}>
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-6 h-6" />
                    <span className="text-[10px] mt-1">Adicionar</span>
                  </>
                )}
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-wait"
                  disabled={uploading}
                  data-testid="fotos-adicionar-input"
                  onChange={(e) => {
                    // IMPORTANTE: snapshot via Array.from ANTES de limpar o input.
                    // e.target.files é uma referência viva — setar value="" esvazia
                    // o FileList in-place, e addFiles receberia uma lista vazia.
                    const picked = Array.from(e.target.files || []);
                    e.target.value = "";
                    if (picked.length > 0) addFiles(picked);
                  }}
                />
              </label>
            )}
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Toque na imagem e arraste para mudar a ordem.</span>
            <span className="font-semibold">{items.length}/{MAX_PRODUCT_IMAGES}</span>
          </div>
        </div>
    </ModalShell>
  );
}

// Os dias da semana com faixas de horário: o «Por encomenda» (a data que o cliente agenda) e o «Horário de venda» (quando
// ele pode pedir na hora) usam o mesmo editor.
function EditorDeDiasDaSemana({ dias, onChange }: { dias: Record<number, ProductAvailabilityWindow[]>; onChange: (v: Record<number, ProductAvailabilityWindow[]>) => void }) {
  const alternarDia = (dow: number) => {
    const cur = { ...dias };
    if (cur[dow]) delete cur[dow];
    else cur[dow] = [{ start: "08:00", end: "18:00" }];
    onChange(cur);
  };
  const mudarFaixa = (dow: number, idx: number, field: "start" | "end", value: string) => {
    const cur = { ...dias };
    const ranges = [...(cur[dow] ?? [])];
    ranges[idx] = { ...ranges[idx], [field]: value } as ProductAvailabilityWindow;
    cur[dow] = ranges;
    onChange(cur);
  };
  const somarFaixa = (dow: number) => {
    const cur = { ...dias };
    cur[dow] = [...(cur[dow] ?? []), { start: "08:00", end: "18:00" }];
    onChange(cur);
  };
  const tirarFaixa = (dow: number, idx: number) => {
    const cur = { ...dias };
    const ranges = [...(cur[dow] ?? [])];
    ranges.splice(idx, 1);
    if (ranges.length === 0) delete cur[dow];
    else cur[dow] = ranges;
    onChange(cur);
  };
  return (
    <div className="space-y-2">
      {DAY_OF_WEEK_LABELS.map((label, dow) => {
        const ranges = dias[dow];
        const selected = !!ranges;
        return (
          <div key={dow} className="rounded-lg border border-border/60 p-2.5" data-testid={`dias-da-semana-${dow}`}>
            <div className="flex items-center justify-between gap-2">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input type="checkbox" checked={selected} onChange={() => alternarDia(dow)} className="accent-primary" />
                {label}
              </label>
              {selected && (
                <button type="button" onClick={() => somarFaixa(dow)} className="text-xs text-primary hover:underline flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Intervalo
                </button>
              )}
            </div>
            {selected && (
              <div className="mt-2 space-y-1.5">
                {(ranges ?? []).map((r, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                    <Input type="time" value={r.start} onChange={e => mudarFaixa(dow, idx, "start", e.target.value)} className="w-28 h-8 text-xs" />
                    <span className="text-xs text-muted-foreground">até</span>
                    <Input type="time" value={r.end} onChange={e => mudarFaixa(dow, idx, "end", e.target.value)} className="w-28 h-8 text-xs" />
                    {(ranges ?? []).length > 1 && (
                      <button type="button" onClick={() => tirarFaixa(dow, idx)} className="text-muted-foreground hover:text-destructive">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AvailabilitySection(props: {
  enabled: boolean;
  onEnabledChange: (v: boolean) => void;
  mode: ProductAvailabilityConfigMode;
  onModeChange: (m: ProductAvailabilityConfigMode) => void;
  weeklyDays: Record<number, ProductAvailabilityWindow[]>;
  onWeeklyDaysChange: (v: Record<number, ProductAvailabilityWindow[]>) => void;
  monthlyDays: number[];
  onMonthlyDaysChange: (v: number[]) => void;
  monthlyRanges: ProductAvailabilityWindow[];
  onMonthlyRangesChange: (v: ProductAvailabilityWindow[]) => void;
  specificDates: string[];
  onSpecificDatesChange: (v: string[]) => void;
  specificRanges: ProductAvailabilityWindow[];
  onSpecificRangesChange: (v: ProductAvailabilityWindow[]) => void;
  newSpecificDate: string;
  onNewSpecificDateChange: (v: string) => void;
}) {
  const toggleMonthDay = (d: number) => {
    if (props.monthlyDays.includes(d)) props.onMonthlyDaysChange(props.monthlyDays.filter(x => x !== d));
    else props.onMonthlyDaysChange([...props.monthlyDays, d].sort((a, b) => a - b));
  };
  const updateRanges = (list: ProductAvailabilityWindow[], idx: number, field: "start" | "end", value: string) => {
    const next = [...list];
    next[idx] = { ...next[idx], [field]: value } as ProductAvailabilityWindow;
    return next;
  };
  const addSpecificDate = () => {
    const d = props.newSpecificDate;
    if (!d) return;
    if (!props.specificDates.includes(d)) {
      props.onSpecificDatesChange([...props.specificDates, d].sort());
    }
    props.onNewSpecificDateChange("");
  };
  const removeSpecificDate = (d: string) => {
    props.onSpecificDatesChange(props.specificDates.filter(x => x !== d));
  };

  return (
    <div className="space-y-3 pl-3 border-t border-border/30 pt-3">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2 text-sm">
          <CalendarClock className="w-4 h-4 text-muted-foreground" />
          Disponibilidade limitada a dias e horários específicos
        </Label>
        <Switch checked={props.enabled} onCheckedChange={props.onEnabledChange} />
      </div>
      <p className="text-xs text-muted-foreground -mt-1">Quando ativo, o cliente só poderá agendar nos dias e horários definidos abaixo.</p>

      {props.enabled && (
        <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
          <div className="flex flex-wrap gap-2">
            {(["weekly", "monthly", "specific_dates"] as ProductAvailabilityConfigMode[]).map(m => (
              <button
                key={m}
                type="button"
                onClick={() => props.onModeChange(m)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${props.mode === m ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-muted"}`}
              >
                {m === "weekly" ? "Dias da semana" : m === "monthly" ? "Dias do mês" : "Datas específicas"}
              </button>
            ))}
          </div>

          {props.mode === "weekly" && <EditorDeDiasDaSemana dias={props.weeklyDays} onChange={props.onWeeklyDaysChange} />}

          {props.mode === "monthly" && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Dias do mês</Label>
                <div className="grid grid-cols-7 gap-1 mt-1.5">
                  {Array.from({ length: 31 }).map((_, i) => {
                    const d = i + 1;
                    const selected = props.monthlyDays.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleMonthDay(d)}
                        className={`aspect-square rounded text-xs font-medium border transition-colors ${selected ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-muted"}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs">Horários</Label>
                <div className="space-y-1.5 mt-1.5">
                  {props.monthlyRanges.map((r, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                      <Input type="time" value={r.start} onChange={e => props.onMonthlyRangesChange(updateRanges(props.monthlyRanges, idx, "start", e.target.value))} className="w-28 h-8 text-xs" />
                      <span className="text-xs text-muted-foreground">até</span>
                      <Input type="time" value={r.end} onChange={e => props.onMonthlyRangesChange(updateRanges(props.monthlyRanges, idx, "end", e.target.value))} className="w-28 h-8 text-xs" />
                      {props.monthlyRanges.length > 1 && (
                        <button type="button" onClick={() => props.onMonthlyRangesChange(props.monthlyRanges.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => props.onMonthlyRangesChange([...props.monthlyRanges, { start: "08:00", end: "18:00" }])} className="text-xs text-primary hover:underline flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Intervalo
                  </button>
                </div>
              </div>
            </div>
          )}

          {props.mode === "specific_dates" && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Datas</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <DataBr value={props.newSpecificDate} onChange={props.onNewSpecificDateChange} className="w-44 h-9 text-xs" />
                  <Button type="button" size="sm" variant="outline" onClick={addSpecificDate}>Adicionar</Button>
                </div>
                {props.specificDates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {props.specificDates.map(d => (
                      <span key={d} className="inline-flex items-center gap-1 text-xs bg-muted rounded-full px-2 py-1">
                        {formatAvailabilityDateLabel(d)}
                        <button type="button" onClick={() => removeSpecificDate(d)} className="text-muted-foreground hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Horários</Label>
                <div className="space-y-1.5 mt-1.5">
                  {props.specificRanges.map((r, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                      <Input type="time" value={r.start} onChange={e => props.onSpecificRangesChange(updateRanges(props.specificRanges, idx, "start", e.target.value))} className="w-28 h-8 text-xs" />
                      <span className="text-xs text-muted-foreground">até</span>
                      <Input type="time" value={r.end} onChange={e => props.onSpecificRangesChange(updateRanges(props.specificRanges, idx, "end", e.target.value))} className="w-28 h-8 text-xs" />
                      {props.specificRanges.length > 1 && (
                        <button type="button" onClick={() => props.onSpecificRangesChange(props.specificRanges.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-destructive">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => props.onSpecificRangesChange([...props.specificRanges, { start: "08:00", end: "18:00" }])} className="text-xs text-primary hover:underline flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Intervalo
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Sistema de seções-cartão do modal de produto ---

function SectionCard({ icon, title, hint, children }: { icon: ReactNode; title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="mb-3.5 flex items-center gap-2.5 border-b border-border/60 pb-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">{icon}</span>
        <span className="font-display text-[15px] font-bold leading-none">{title}</span>
        {hint ? <span className="ml-auto text-[11px] font-medium text-muted-foreground">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

function ToggleCard({ icon, title, subtitle, checked, onCheckedChange, accent, children, badge, switchDisabled }: { icon: ReactNode; title: string; subtitle?: string; checked: boolean; onCheckedChange: (v: boolean) => void; accent?: boolean; children?: ReactNode; badge?: ReactNode; switchDisabled?: boolean }) {
  const hasBody = checked && !!children;
  return (
    <div className={`rounded-2xl border bg-card p-4 shadow-sm transition-colors ${checked ? (accent ? "border-accent/40" : "border-primary/40") : "border-border"}`}>
      <div className={`flex items-center gap-2.5 ${hasBody ? "mb-3.5 border-b border-border/60 pb-3" : ""}`}>
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${checked ? (accent ? "bg-accent/15 text-accent" : "bg-primary/15 text-primary") : "bg-muted text-muted-foreground"}`}>{icon}</span>
        <div className="min-w-0">
          <div className="font-display text-sm font-bold leading-tight">{title}</div>
          {subtitle ? <div className="text-[11px] leading-snug text-muted-foreground">{subtitle}</div> : null}
          {badge ? <div className="mt-1">{badge}</div> : null}
        </div>
        <div className="ml-auto shrink-0"><Switch checked={checked} onCheckedChange={onCheckedChange} disabled={switchDisabled} /></div>
      </div>
      {hasBody ? <div className="animate-in fade-in slide-in-from-top-1">{children}</div> : null}
    </div>
  );
}

function OptionSegmented({ value, onChange }: { value: "single" | "multiple"; onChange: (v: "single" | "multiple") => void }) {
  const opts = [
    { v: "single" as const, label: "Escolha única", icon: <CircleDot className="h-3.5 w-3.5" /> },
    { v: "multiple" as const, label: "Múltipla", icon: <CheckSquare className="h-3.5 w-3.5" /> },
  ];
  return (
    <div className="flex gap-0.5 rounded-lg bg-muted p-0.5">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-xs font-semibold transition-colors ${value === o.v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
        >
          {o.icon} {o.label}
        </button>
      ))}
    </div>
  );
}

// Stepper do "Máximo de seleções": vazio = sem limite (∞). "+" a partir do ∞ começa
// em 1 (passa a limitar); "−" de 1 volta pro ilimitado; "−" no ∞ é no-op.
// emptyLabel: o vazio quer dizer coisas diferentes nos dois usos — no MÁXIMO é
// "sem teto" (∞), no MÍNIMO é "1" (uma escolha basta). ∞ como mínimo não existe.
function MaxStepper({ value, onChange, emptyLabel = "∞" }: { value: string; onChange: (v: string) => void; emptyLabel?: string }) {
  const n = parseInt(value, 10);
  const current = Number.isFinite(n) && n >= 1 ? n : null;
  const dec = () => onChange(current === null ? "" : current <= 1 ? "" : String(current - 1));
  const inc = () => onChange(current === null ? "1" : String(current + 1));
  return (
    <div className="inline-flex h-8 items-center rounded-lg border border-border bg-background">
      <button type="button" onClick={dec} className="flex h-full w-7 items-center justify-center text-base text-primary">−</button>
      <span className="w-7 text-center text-sm font-bold">{current === null ? emptyLabel : current}</span>
      <button type="button" onClick={inc} className="flex h-full w-7 items-center justify-center text-base text-primary">+</button>
    </div>
  );
}

function ProductModal({ isOpen, onClose, product, categories, onOpenIngredientsManager, onEditProduct }: { isOpen: boolean, onClose: () => void, product: Product | null, categories: Category[], onOpenIngredientsManager: () => void, onEditProduct?: (id: string) => void }) {
  const [formData, setFormData] = useState({
    name: "", description: "", price: "", stock: "", categoryId: "", status: "ativo", servings: "", slices: ""
  });
  // Código do produto: barras (forma natural na tela; o servidor grava 14
  // dígitos) ou código próprio da loja (vai como está, em maiúsculas).
  const [barcode, setBarcode] = useState("");
  const ultra = useIsUltra();
  const podeBarcode = useFeatureAllowed("barcode");
  // Produto novo abre com o foco no código de barras — só onde há mouse
  // (desktop): o leitor USB bipa direto, sem clique. No celular/tablet o foco
  // abriria o teclado em cima do modal (decisão do Pedro, 10/09).
  const desktopPointer = useMemo(() => typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches, []);
  // Estoque em kg (venda por peso). String com vírgula; vazio = sem controle.
  const [stockKgStr, setStockKgStr] = useState("");
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [squareUploading, setSquareUploading] = useState(false);
  // Vídeo do produto (Profissional e Ultra): o id vai no «Salvar Produto», com o lugar dele entre as fotos e a capa.
  const podeVideo = useFeatureAllowed("product_video");
  const videoEnvio = useEnvioDeVideo();
  const [video, setVideo] = useState<{ id: string; posicao: number; capa: number } | null>(null);
  // O vídeo com que o produto abriu: tirar este manda `video: null`; os novos que saem só param de enviar.
  const [videoInicial, setVideoInicial] = useState<string | null>(null);
  const [avisoVideoPlano, setAvisoVideoPlano] = useState(false);
  const [noStockControl, setNoStockControl] = useState(false);
  // Override opcional do "A partir de" (só relevante quando preço-base é 0).
  const [startingPriceOverride, setStartingPriceOverride] = useState("");
  const [showOptional, setShowOptional] = useState(false);
  const [showNewCat, setShowNewCat] = useState(false);
  // Categoria sugerida pela leitura do código: abre o campo já preenchido,
  // mas SEM roubar o foco — o autoFocus rolava o modal até "Organização" e
  // o lojista perdia de vista o nome e as fotos que acabaram de chegar.
  const [newCatSilent, setNewCatSilent] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const [editCatName, setEditCatName] = useState("");

  const [isFood, setIsFood] = useState(true);
  const [optionGroups, setOptionGroups] = useState<OptionGroupForm[]>([]);
  const [removableIngredients, setRemovableIngredients] = useState<string[]>([]);
  const [newIngredient, setNewIngredient] = useState("");
  const [isSuggestive, setIsSuggestive] = useState(false);
  const [isVegan, setIsVegan] = useState(false);
  const [soldByWeight, setSoldByWeight] = useState(false);
  // Porções pré-definidas (kg) e venda no link. Digitação em kg com vírgula.
  // Linhas do editor de porções (padrão dos adicionais): peso + preço
  // opcional (vazio = calculado peso × R$/kg na venda).
  const [portionRows, setPortionRows] = useState<Array<{ kgStr: string; priceStr: string; priceTouched?: boolean }>>([]);
  // Modo do peso: fracionado (cliente digita o peso) ou porções pré-definidas.
  const [modoPorcoes, setModoPorcoes] = useState(false);
  const [weightPublic, setWeightPublic] = useState(false);
  // «ml» ao lado de kg e g (T-0056, 04/11/2026: decants de perfume): g e ml digitam em unidades (1 ml = 1 g = 0,001 kg);
  // no ml o preço é por litro e o estoque também se digita em ml. O canônico continua kg.
  const [weightUnit, setWeightUnit] = useState<"kg" | "g" | "ml">("kg");
  const emUnidades = weightUnit !== "kg";
  const [costPrice, setCostPrice] = useState("");
  const [isOrderBased, setIsOrderBased] = useState(false);
  // Horário de venda para quem compra na hora (T-0060, Pedro, 04/11/2026: «com um toggle escondendo no produto, para não
  // ficar visível e enchendo a tela»): só no produto que não é Por encomenda; desligado, nada aparece e vai null.
  const [vendaComHorario, setVendaComHorario] = useState(false);
  const [diasDeVenda, setDiasDeVenda] = useState<Record<number, ProductAvailabilityWindow[]>>({});
  // Valor da antecedência na unidade escolhida: "dias" conta o calendário ("1 dia" = qualquer horário de
  // amanhã), "horas" conta da hora do pedido ("24 horas" = pediu 23h, só a partir de 23h de amanhã) — Pedro, 03/10/2026.
  const [antecedencia, setAntecedencia] = useState("");
  const [advanceUnit, setAdvanceUnit] = useState<"dias" | "horas">("dias");
  const [dailyCapacity, setDailyCapacity] = useState("");
  // «Aceitar encomendas» de/até (Pedro, 26/10/2026): o primeiro e o último dia que o link aceita, "AAAA-MM-DD"; vazio =
  // sem limite. Início depois do fim não salva (o servidor também recusa).
  const [orderFromDate, setOrderFromDate] = useState("");
  const [orderUntilDate, setOrderUntilDate] = useState("");
  const janelaInvalida = !!orderFromDate && !!orderUntilDate && orderFromDate > orderUntilDate;
  // O que o cliente escolhe na encomenda (12/10/2026, aprovado pelo Pedro): horário (padrão), turno ou só a data.
  const [scheduleMode, setScheduleMode] = useState<ModoEncomenda>("horario");
  const [shifts, setShifts] = useState<Turno[]>([...TURNOS]);
  const [shiftCapacity, setShiftCapacity] = useState("");
  // «Usar em todos os produtos por encomenda» (Pedro, 12/10/2026: toggle no lugar do link). Vem desligado toda vez
  // que o produto abre e só vale no «Salvar Produto», depois da confirmação.
  const [usarEmTodos, setUsarEmTodos] = useState(false);
  const { data: settingsDaLoja } = useQuery<any>({ queryKey: ["settings"], queryFn: () => customFetch("/api/settings"), staleTime: 60_000 });
  const horariosDosTurnos = horarioDosTurnos(settingsDaLoja?.orderShiftHours, settingsDaLoja?.businessHours);
  // Turno desligado na loja (Configurações → Produtos, ou fora do horário de funcionamento) não aparece no card.
  const turnosDaLoja = TURNOS.filter((t) => horariosDosTurnos[t].ligado);
  const [limitPerCart, setLimitPerCart] = useState(false);
  const [maxPerCart, setMaxPerCart] = useState("");
  // Pacote de escolhas (Pedro, 15/10/2026; nasceu «Contar preparos»; recurso do Profissional): N inclusos, até M, R$ por
  // item a mais e como a loja chama os itens.
  const [prepEnabled, setPrepEnabled] = useState(false);
  const [prepIncluded, setPrepIncluded] = useState("");
  const [prepMax, setPrepMax] = useState("");
  const [prepExtra, setPrepExtra] = useState("");
  const [prepUnit, setPrepUnit] = useState("");
  // «Até N no preço» (15/10/2026, caso Meu macarrão): ligado, o cliente marca de 0 a N e o extra conta só acima de N.
  const [prepPodeMenos, setPrepPodeMenos] = useState(false);
  // «Cada lista conta separado» (Pedro, 28/10/2026, Açaí beat).
  const [prepPorLista, setPrepPorLista] = useState(false);
  const podePreparos = useFeatureAllowed("prep_count");
  // Adicionais fracionados (Profissional): ligado, cada opção mostra «Conta como ½ | 1 | 2».
  const [fracionados, setFracionados] = useState(false);
  const podePesos = useFeatureAllowed("option_weight");
  const pagaPlano = useIsPaidPlan();

  const [availabilityEnabled, setAvailabilityEnabled] = useState(false);
  const [availabilityMode, setAvailabilityMode] = useState<ProductAvailabilityConfigMode>("weekly");
  const [weeklyDays, setWeeklyDays] = useState<Record<number, ProductAvailabilityWindow[]>>({});
  const [monthlyDays, setMonthlyDays] = useState<number[]>([]);
  const [monthlyRanges, setMonthlyRanges] = useState<ProductAvailabilityWindow[]>([{ start: "08:00", end: "18:00" }]);
  const [specificDates, setSpecificDates] = useState<string[]>([]);
  const [specificRanges, setSpecificRanges] = useState<ProductAvailabilityWindow[]>([{ start: "08:00", end: "18:00" }]);
  const [newSpecificDate, setNewSpecificDate] = useState("");

  const [promoEnabled, setPromoEnabled] = useState(false);
  const [promoDiscountType, setPromoDiscountType] = useState<"percent" | "fixed">("percent");
  const [promoDiscountValue, setPromoDiscountValue] = useState("");
  const [promoScheduleType, setPromoScheduleType] = useState<"always" | "weekly" | "range">("always");
  const [promoWeekDays, setPromoWeekDays] = useState<number[]>([]);
  const [promoHasTimeWindow, setPromoHasTimeWindow] = useState(false);
  const [promoStartTime, setPromoStartTime] = useState("");
  const [promoEndTime, setPromoEndTime] = useState("");
  const [promoStartsAt, setPromoStartsAt] = useState("");
  const [promoEndsAt, setPromoEndsAt] = useState("");
  // Promoção por quantidade (kind "quantity") + canal (vale pros dois kinds)
  const [promoKind, setPromoKind] = useState<"simple" | "quantity">("simple");
  const [promoChannels, setPromoChannels] = useState<"both" | "pdv" | "link">("both");
  const [promoQtyMode, setPromoQtyMode] = useState<"threshold" | "all_from" | "every_nth" | "progressive">("threshold");
  const [promoQtyFrom, setPromoQtyFrom] = useState("2");
  const [promoQtyN, setPromoQtyN] = useState("3");
  const [promoQtyDiscType, setPromoQtyDiscType] = useState<"percent" | "fixed">("percent");
  const [promoQtyDiscValue, setPromoQtyDiscValue] = useState("");
  const [promoQtyStepType, setPromoQtyStepType] = useState<"percent" | "fixed">("percent");
  const [promoQtyStepValue, setPromoQtyStepValue] = useState("");
  const [promoQtyMax, setPromoQtyMax] = useState("");

  const { toast } = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const uploadMutation = useUploadImage();

  // Upload direto pelo quadrado de imagem (abre a galeria sem abrir o modal —
  // o modal aninhado nao abria de forma confiavel no mobile).
  const handleSquareImageFiles = async (files: File[], comVideo = !!video) => {
    const vagas = MAX_PRODUCT_IMAGES - (comVideo ? 1 : 0);
    setSquareUploading(true);
    try {
      const uploaded = await uploadProductImageFiles(files, vagas - imageUrls.length, uploadMutation, toast);
      if (uploaded.length > 0) {
        setImageUrls((prev) => [...prev, ...uploaded].slice(0, vagas));
      }
    } finally {
      setSquareUploading(false);
    }
  };

  const descartarSeNovo = (id: string) => {
    if (id !== videoInicial) videoEnvio.cancelar(id);
  };

  // O mesmo botão das fotos aceita o vídeo (Pedro, 24/10/2026): sem o plano, avisa e não envia.
  const escolherVideo = async (arquivo: File): Promise<string | null> => {
    if (!podeVideo) {
      setAvisoVideoPlano(true);
      return null;
    }
    setAvisoVideoPlano(false);
    return videoEnvio.comecar(arquivo);
  };

  const handleSquareFiles = async (picked: File[]) => {
    const videos = picked.filter(ehArquivoDeVideo);
    const fotos = picked.filter((f) => !ehArquivoDeVideo(f));
    let comVideo = !!video;
    if (videos.length > 0) {
      if (videos.length > 1) toast({ title: "Só 1 vídeo por produto", description: "Fica o primeiro que você escolheu." });
      if (!video && imageUrls.length >= MAX_PRODUCT_IMAGES) {
        toast({ title: "Limite atingido", description: `O produto pode ter no máximo ${MAX_PRODUCT_IMAGES} fotos, contando o vídeo. Tire uma foto para pôr o vídeo.`, variant: "destructive" });
      } else {
        setSquareUploading(true);
        try {
          const id = await escolherVideo(videos[0]!);
          if (id) {
            if (video) descartarSeNovo(video.id);
            setVideo((v) => ({ id, posicao: v ? v.posicao : imageUrls.length, capa: 0 }));
            comVideo = true;
          }
        } finally {
          setSquareUploading(false);
        }
      }
    }
    if (fotos.length > 0) await handleSquareImageFiles(fotos, comVideo);
  };

  const itensDoEditor = useMemo<ItemDoEditor[]>(() => {
    const lista: ItemDoEditor[] = imageUrls.map((url) => ({ tipo: "foto", url }));
    if (video) lista.splice(Math.min(video.posicao, lista.length), 0, { tipo: "video", id: video.id });
    return lista;
  }, [imageUrls, video]);

  const aplicarItens = (itens: ItemDoEditor[]) => {
    const i = itens.findIndex((it) => it.tipo === "video");
    const novo = i >= 0 ? (itens[i] as { tipo: "video"; id: string }).id : null;
    if (video && video.id !== novo) descartarSeNovo(video.id);
    setImageUrls(itens.flatMap((it) => (it.tipo === "foto" ? [it.url] : [])));
    setVideo(novo ? { id: novo, posicao: i, capa: video && video.id === novo ? video.capa : 0 } : null);
  };

  const videoSubindo = !!video && !!videoEnvio.envios[video.id]?.subindo;

  // ["settings"] junto: «Horário dos turnos» (Configurações) só aparece com produto em Turno.
  const createMutation = useCreateProduct({
    mutation: { onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() }); queryClient.invalidateQueries({ queryKey: ["settings"] }); onClose(); toast({title:"Salvo!"}) } }
  });
  const updateMutation = useUpdateProduct({
    mutation: { onSuccess: () => { queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() }); queryClient.invalidateQueries({ queryKey: ["settings"] }); onClose(); toast({title:"Salvo!"}) } }
  });
  const createCatMutation = useCreateCategory({
    mutation: {
      onSuccess: (newCat) => {
        queryClient.invalidateQueries({ queryKey: getListCategoriesQueryKey() });
        setShowNewCat(false);
        setNewCatName("");
        if (newCat?.id) setFormData((f) => ({ ...f, categoryId: newCat.id }));
        toast({ title: "Categoria criada!" });
      },
    },
  });
  const updateCatMutation = useUpdateCategory({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCategoriesQueryKey() });
        setEditingCatId(null);
        setEditCatName("");
        toast({ title: "Categoria atualizada!" });
      },
    },
  });
  const deleteCatMutation = useDeleteCategory({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCategoriesQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
        setFormData((f) => ({ ...f, categoryId: "" }));
        toast({ title: "Categoria excluída!" });
      },
    },
  });

  useEffect(() => {
    if (!isOpen) videoEnvio.limpar();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      videoEnvio.limpar();
      setAvisoVideoPlano(false);
      if (product) {
        const p = product as any;
        const ev = estadoDoVideo(p.video);
        if (ev) videoEnvio.registrar(ev);
        setVideo(ev ? { id: ev.id, posicao: ev.posicao, capa: ev.capa } : null);
        setVideoInicial(ev?.id ?? null);
        setFormData({
          name: product.name,
          description: product.description || "",
          price: product.price.toString(),
          stock: product.stock !== null ? product.stock.toString() : "",
          categoryId: product.categoryId || "",
          status: product.status,
          servings: p.servings ? String(p.servings) : "",
          slices: p.slices ? String(p.slices) : ""
        });
        const initialUrls: string[] = Array.isArray(p.imageUrls) && p.imageUrls.length > 0
          ? p.imageUrls.filter((u: any): u is string => typeof u === "string" && u.length > 0).slice(0, MAX_PRODUCT_IMAGES)
          : (product.imageUrl && !p.video ? [product.imageUrl] : []);
        setImageUrls(initialUrls);
        setNoStockControl(product.soldByWeight ? (product as any).stockKg == null : product.stock === null);
        setStartingPriceOverride(
          p.startingPriceOverride !== null && p.startingPriceOverride !== undefined
            ? String(p.startingPriceOverride)
            : "",
        );
        setShowOptional(!!p.servings);
        setIsFood(p.isFood ?? true);
        setBarcode(formatarCodigoProduto(p.barcode));
        setOptionGroups((p.optionGroups || []).map((g: any) => ({
          id: nextOptionUid(),
          gid: g.id,
          name: g.name,
          selectionType: g.selectionType,
          maxSelections: g.maxSelections ? String(g.maxSelections) : "",
          minSelections: (g as any).minSelections ? String((g as any).minSelections) : "",
          required: g.required,
          onePerOption: g.onePerOption ?? false,
          priceMode: g.priceMode || "sum",
          optionType: g.optionType || "personalizado",
          ...(g.inPackage === false ? { inPackage: false } : {}),
          options: g.options.map((o: any) => {
            const stockStr = o.stock !== null && o.stock !== undefined ? String(o.stock) : "";
            // Peso da opção; o legado do grupo (primeiro «Contar preparos») vale para a opção sem o próprio; o guardado
            // com o interruptor desligado (`countAsOff`) volta ao religar.
            const valido = (w: unknown) => (w === 0.5 || w === 2 ? w : null);
            const peso = valido(o.countAs) ?? valido(g.prepWeight) ?? valido(o.countAsOff) ?? 1;
            return { id: nextOptionUid(), oid: o.id, name: o.name, price: String(o.price || 0), stock: stockStr, stockBefore: stockStr, ingredientId: o.ingredientId ?? undefined, countAs: peso, ...(o.inPackage === false ? { inPackage: false } : {}) };
          }),
        })));
        setRemovableIngredients(p.removableIngredients || []);
        setIsSuggestive(p.isSuggestive ?? false);
        setIsVegan(p.isVegan === true);
        setSoldByWeight(p.soldByWeight ?? false);
        {
          const emG = (p as any).weightUnit === "g" || (p as any).weightUnit === "ml";
          const pcs = parsePorcoes(p.portions);
          const perKgLoad = parseFloat(String((p as any).price ?? ""));
          setPortionRows(pcs.map((o) => ({
            kgStr: emG ? String(Math.round(o.kg * 1000)) : String(o.kg).replace(".", ","),
            // Preço sempre visível: fixado mostra o fixado; calculado mostra o
            // cálculo (e segue o peso até o lojista mexer no preço).
            priceStr: o.price != null
              ? String(o.price.toFixed(2)).replace(".", ",")
              : (Number.isFinite(perKgLoad) ? String((Math.round(o.kg * perKgLoad * 100) / 100).toFixed(2)).replace(".", ",") : ""),
            priceTouched: o.price != null,
          })));
          setModoPorcoes(pcs.length > 0);
        }
        setWeightPublic((p as any).weightPublic === true);
        setWeightUnit((p as any).weightUnit === "g" || (p as any).weightUnit === "ml" ? (p as any).weightUnit : "kg");
    setStockKgStr(
      p.stockKg != null
        ? (p as any).weightUnit === "ml"
          ? String(Math.round(parseFloat(String(p.stockKg)) * 1000))
          : String(parseFloat(String(p.stockKg))).replace(".", ",")
        : "",
    );
        setCostPrice(p.costPrice != null ? String(p.costPrice).replace(".", ",") : "");
        setIsOrderBased(p.isOrderBased ?? false);
        {
          const sh = (p as any).saleHours as { weekly?: Array<{ dayOfWeek: number; ranges?: ProductAvailabilityWindow[] }> } | null | undefined;
          const dias: Record<number, ProductAvailabilityWindow[]> = {};
          for (const d of sh?.weekly ?? []) if (d.ranges?.length) dias[d.dayOfWeek] = d.ranges.map((r) => ({ start: r.start, end: r.end }));
          setDiasDeVenda(dias);
          setVendaComHorario(Object.keys(dias).length > 0);
        }
        setAdvanceUnit((p as any).minAdvanceHours ? "horas" : "dias");
        setAntecedencia((p as any).minAdvanceHours ? String((p as any).minAdvanceHours) : p.minAdvanceDays ? String(p.minAdvanceDays) : "");
        setDailyCapacity(p.dailyCapacity ? String(p.dailyCapacity) : "");
        setOrderFromDate(typeof p.orderFromDate === "string" ? p.orderFromDate.slice(0, 10) : "");
        setOrderUntilDate(typeof p.orderUntilDate === "string" ? p.orderUntilDate.slice(0, 10) : "");
        setScheduleMode(modoDaEncomenda(p as any));
        setUsarEmTodos(false);
        setShifts(turnosDoProduto(p as any));
        setShiftCapacity((p as any).orderShiftCapacity ? String((p as any).orderShiftCapacity) : "");
        setLimitPerCart(!!(p as any).maxPerCart);
        setMaxPerCart((p as any).maxPerCart ? String((p as any).maxPerCart) : "");
        const pc = (p as any).prepCount as { enabled?: boolean; included?: number; max?: number | null; extraPrice?: number; unitName?: string | null; required?: boolean; porLista?: boolean } | null | undefined;
        setPrepEnabled(!!pc?.enabled);
        setPrepUnit(pc?.unitName ?? "");
        setPrepPodeMenos(pc?.required === false);
        setPrepPorLista(pc?.porLista === true);
        setFracionados(
          ((p.optionGroups || []) as any[]).some(
            (g) => g?.prepWeight === 0.5 || g?.prepWeight === 2 || (g?.options || []).some((o: any) => o?.countAs === 0.5 || o?.countAs === 2),
          ),
        );
        setPrepIncluded(pc?.included != null ? String(pc.included) : "");
        setPrepMax(pc?.max != null ? String(pc.max) : "");
        setPrepExtra(pc?.extraPrice != null ? String(pc.extraPrice) : "");
        const ac: ProductAvailabilityConfig | null | undefined = (p as any).availabilityConfig;
        setAvailabilityEnabled(!!ac?.enabled);
        setAvailabilityMode((ac?.mode as ProductAvailabilityConfigMode) ?? "weekly");
        const wkMap: Record<number, ProductAvailabilityWindow[]> = {};
        (ac?.weekly ?? []).forEach(d => { wkMap[d.dayOfWeek] = d.ranges ?? []; });
        setWeeklyDays(wkMap);
        setMonthlyDays(ac?.monthly?.days ?? []);
        setMonthlyRanges(ac?.monthly?.ranges?.length ? ac.monthly.ranges : [{ start: "08:00", end: "18:00" }]);
        setSpecificDates(ac?.specificDates?.dates ?? []);
        setSpecificRanges(ac?.specificDates?.ranges?.length ? ac.specificDates.ranges : [{ start: "08:00", end: "18:00" }]);
        const promo = p.promotion || null;
        setPromoEnabled(!!promo?.enabled);
        setPromoDiscountType(promo?.discountType === "fixed" ? "fixed" : "percent");
        setPromoDiscountValue(promo?.discountValue !== undefined && promo?.discountValue !== null ? String(promo.discountValue) : "");
        setPromoScheduleType(promo?.scheduleType === "weekly" ? "weekly" : promo?.scheduleType === "range" ? "range" : "always");
        setPromoWeekDays(promo?.weekly?.days || []);
        setPromoHasTimeWindow(!!promo?.weekly?.hasTimeWindow);
        setPromoStartTime(promo?.weekly?.startTime || "");
        setPromoEndTime(promo?.weekly?.endTime || "");
        setPromoStartsAt(promo?.range?.startsAt ? String(promo.range.startsAt).slice(0, 16) : "");
        setPromoEndsAt(promo?.range?.endsAt ? String(promo.range.endsAt).slice(0, 16) : "");
        const pAny = promo as any;
        setPromoKind(pAny?.kind === "quantity" ? "quantity" : "simple");
        setPromoChannels(pAny?.channels === "pdv" || pAny?.channels === "link" ? pAny.channels : "both");
        const q = pAny?.quantity;
        setPromoQtyMode(q?.mode === "every_nth" ? "every_nth" : q?.mode === "progressive" ? "progressive" : q?.mode === "all_from" ? "all_from" : "threshold");
        setPromoQtyFrom(q?.fromUnit ? String(q.fromUnit) : "2");
        setPromoQtyN(q?.n ? String(q.n) : "3");
        setPromoQtyDiscType(q?.discountType === "fixed" ? "fixed" : "percent");
        setPromoQtyDiscValue(q?.discountValue != null ? String(q.discountValue) : "");
        setPromoQtyStepType(q?.step?.discountType === "fixed" ? "fixed" : "percent");
        setPromoQtyStepValue(q?.step?.discountValue != null ? String(q.step.discountValue) : "");
        setPromoQtyMax(q?.maxDiscount != null ? String(q.maxDiscount) : "");
      } else {
        setFormData({ name: "", description: "", price: "", stock: "", categoryId: "", status: "ativo", servings: "", slices: "" });
        setStockKgStr("");
        setImageUrls([]);
        setVideo(null);
        setVideoInicial(null);
        setNoStockControl(false);
        setStartingPriceOverride("");
        setShowOptional(false);
        // localStorage lanca SecurityError em navegador in-app (WebView do
        // WhatsApp) que bloqueia storage — derrubava o /produtos ao abrir o form.
        // Fail-safe pro default (isFood=true).
        let savedPref: string | null = null;
        try { savedPref = localStorage.getItem(FOOD_PREF_KEY); } catch { /* storage bloqueado */ }
        setIsFood(savedPref !== null ? savedPref === "true" : true);
        setOptionGroups([]);
        setRemovableIngredients([]);
        setNewIngredient("");
        setIsSuggestive(false);
        setIsVegan(false);
        setBarcode("");
        setNewCatSilent(false);
        setSoldByWeight(false);
        setPortionRows([]);
        setModoPorcoes(false);
        setWeightPublic(false);
        setWeightUnit("kg");
        setCostPrice("");
        setIsOrderBased(false);
        setVendaComHorario(false);
        setDiasDeVenda({});
        setAntecedencia("");
        setAdvanceUnit("dias");
        setDailyCapacity("");
        setOrderFromDate("");
        setOrderUntilDate("");
        setScheduleMode("horario");
        setUsarEmTodos(false);
        setShifts([...TURNOS]);
        setShiftCapacity("");
        setLimitPerCart(false);
        setMaxPerCart("");
        setPrepEnabled(false);
        setPrepUnit("");
        setPrepPodeMenos(false);
        setPrepPorLista(false);
        setFracionados(false);
        setPrepIncluded("");
        setPrepMax("");
        setPrepExtra("");
        setAvailabilityEnabled(false);
        setAvailabilityMode("weekly");
        setWeeklyDays({});
        setMonthlyDays([]);
        setMonthlyRanges([{ start: "08:00", end: "18:00" }]);
        setSpecificDates([]);
        setSpecificRanges([{ start: "08:00", end: "18:00" }]);
        setPromoEnabled(false);
        setPromoDiscountType("percent");
        setPromoDiscountValue("");
        setPromoKind("simple");
        setPromoChannels("both");
        setPromoQtyMode("threshold");
        setPromoQtyFrom("2");
        setPromoQtyN("3");
        setPromoQtyDiscType("percent");
        setPromoQtyDiscValue("");
        setPromoQtyStepType("percent");
        setPromoQtyStepValue("");
        setPromoQtyMax("");
        setPromoScheduleType("always");
        setPromoWeekDays([]);
        setPromoHasTimeWindow(false);
        setPromoStartTime("");
        setPromoEndTime("");
        setPromoStartsAt("");
        setPromoEndsAt("");
      }
      setShowNewCat(false);
      setNewCatName("");
      setEditingCatId(null);
      setEditCatName("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product]);

  const handleFoodToggle = (v: boolean) => {
    setIsFood(v);
    try { localStorage.setItem(FOOD_PREF_KEY, String(v)); } catch { /* storage bloqueado */ }
  };

  const addOptionGroup = () => {
    setOptionGroups([...optionGroups, { id: nextOptionUid(), name: "Acompanhamentos", selectionType: "single", maxSelections: "3", minSelections: "", required: false, onePerOption: false, priceMode: "sum", optionType: "ingredientes", options: [{ id: nextOptionUid(), name: "", price: "0", stock: "", stockBefore: "" }], saveAsTemplate: false }]);
  };

  const addOptionGroupFromTemplate = (copy: CopiedGroup) => {
    // O template já traz seu próprio optionType — respeitar (não forçar "personalizado").
    setOptionGroups([...optionGroups, {
      ...copy,
      id: nextOptionUid(),
      // ⚠️ Cópia NUNCA herda identidade: dois produtos com o mesmo id de grupo
      // deixam o pedido sem saber de quem é a opção, e isso "parece funcionar".
      // Sem gid/oid o servidor gera ids novos no salvar.
      gid: undefined,
      optionType: copy.optionType ?? "personalizado",
      onePerOption: copy.onePerOption ?? false,
      // Modelo só semeia piso junto com o obrigatório (ver `pisoDoGrupo`).
      minSelections: copy.required && (copy as { minSelections?: number | string | null }).minSelections
        ? String((copy as { minSelections?: number | string | null }).minSelections)
        : "",
      // Template não carrega estoque (é estado operacional, não configuração).
      options: copy.options.map((o) => ({ ...o, id: nextOptionUid(), oid: undefined, stock: "", stockBefore: "" })),
      saveAsTemplate: false,
    }]);
  };

  // Pacote de escolhas, «Adicionais que contam» (Pedro, 29/10/2026): marca o adicional dentro ou fora do pacote em TODAS
  // as listas que contam (a Nutella do Copo 1 e a do Copo 2 são a mesma ficha).
  const marcarAdicionalNoPacote = (nome: string, dentro: boolean) => {
    setOptionGroups((prev) => prev.map((g) => (g.inPackage === false ? g : {
      ...g,
      options: g.options.map((o) => (o.name.trim() === nome ? { ...o, inPackage: dentro ? undefined : false } : o)),
    })));
  };

  const updateGroup = (idx: number, field: string, value: any) => {
    setOptionGroups(optionGroups.map((g, i) => i === idx ? { ...g, [field]: value } : g));
  };

  const removeGroup = (idx: number) => {
    setOptionGroups(optionGroups.filter((_, i) => i !== idx));
  };

  const addOptionToGroup = (groupIdx: number) => {
    setOptionGroups(optionGroups.map((g, i) => i === groupIdx ? { ...g, options: [...g.options, { id: nextOptionUid(), name: "", price: "0", stock: "", stockBefore: "", ingredientId: undefined }] } : g));
  };

  const updateOptionInGroup = (groupIdx: number, optIdx: number, field: string, value: string) => {
    setOptionGroups(optionGroups.map((g, gi) => gi === groupIdx ? {
      ...g, options: g.options.map((o, oi) => oi === optIdx ? { ...o, [field]: value } : o)
    } : g));
  };

  const setOptionWeight = (groupIdx: number, optIdx: number, w: number) => {
    setOptionGroups(optionGroups.map((g, gi) => gi === groupIdx ? {
      ...g, options: g.options.map((o, oi) => oi === optIdx ? { ...o, countAs: w } : o)
    } : g));
  };

  const removeOptionFromGroup = (groupIdx: number, optIdx: number) => {
    setOptionGroups(optionGroups.map((g, gi) => gi === groupIdx ? { ...g, options: g.options.filter((_, oi) => oi !== optIdx) } : g));
  };

  // Reordena as opções de um grupo (drag-and-drop via SortableList, igual ao
  // reorder de categorias). A ordem do array é a persistida (option_groups é
  // jsonb, sem sortOrder).
  const reorderOptionsInGroup = (groupIdx: number, newOptions: OptionItemForm[]) => {
    setOptionGroups((prev) => prev.map((g, gi) => gi === groupIdx ? { ...g, options: newOptions } : g));
  };

  // Adicionais reordenam por setas no topo do card (arrastar o card grande não
  // ficou bom). Mesma lógica de array-order persistido.
  const moveGroup = (idx: number, dir: -1 | 1) => {
    setOptionGroups((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const addIngredient = () => {
    if (newIngredient.trim() && !removableIngredients.includes(newIngredient.trim())) {
      setRemovableIngredients([...removableIngredients, newIngredient.trim()]);
      setNewIngredient("");
    }
  };

  const erroNoHorarioDeVenda = erroDoHorarioDeVenda(diasDeVenda);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOrderBased && vendaComHorario && erroNoHorarioDeVenda) {
      document.querySelector('[data-testid="product-sale-hours"]')?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    if (isOrderBased && janelaInvalida) {
      document.querySelector('[data-testid="product-order-window"]')?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    // O id só vale depois que o arquivo chegou: o Salvar espera o envio do vídeo.
    if (videoSubindo) return;
    try {

      // Grupos de Opções saíram do gate `isFood` em mai/2026 — ficam na seção
      // principal porque também são usados por produtos não-alimento (ex: combos).
      const cleanedGroups = optionGroups
        .filter(g => {
          if (!g.name.trim()) return false;
          // Opção válida = personalizado com nome OU ingredientes com ingredientId.
          return g.options.some(o => g.optionType === "ingredientes" ? !!o.ingredientId : !!o.name.trim());
        })
        .map(g => ({
          ...(g.gid ? { id: g.gid } : {}),
          name: g.name.trim(),
          selectionType: g.selectionType,
          maxSelections: g.selectionType === "multiple" && g.maxSelections ? parseInt(g.maxSelections, 10) : undefined,
          // ⚠️ Fecha a ORIGEM do piso invisível: o campo vive pendurado no
          // "Obrigatório" (some da tela quando ele desliga), mas o valor
          // continuava no estado do form e voltava pro banco no próximo
          // salvar — regra que o lojista não vê e que barra o cliente dele.
          // Salvar sem obrigatório apaga; e o piso nunca passa do teto.
          minSelections: pisoDoGrupo(g),
          required: g.required,
          onePerOption: g.selectionType === "multiple" ? !!g.onePerOption : false,
          priceMode: g.selectionType === "multiple" ? g.priceMode : "sum",
          optionType: g.optionType,
          ...(g.inPackage === false ? { inPackage: false } : {}),
          options: g.options
            .filter(o => g.optionType === "ingredientes" ? !!o.ingredientId : !!o.name.trim())
            .map(o => ({
              ...(o.oid ? { id: o.oid } : {}),
              name: o.name.trim(),
              price: parseFloat(o.price.replace(',', '.')) || 0,
              // "" = sem controle (null). Só personalizado tem estoque próprio.
              stock: g.optionType === "personalizado" && o.stock.trim() !== "" && Number.isFinite(parseInt(o.stock, 10))
                ? Math.max(0, parseInt(o.stock, 10))
                : null,
              // Transiente: o servidor usa pra decidir se o lojista mexeu no
              // campo (aplica) ou não (preserva o contador vivo do banco).
              stockBefore: g.optionType === "personalizado" && o.stockBefore.trim() !== "" && Number.isFinite(parseInt(o.stockBefore, 10))
                ? Math.max(0, parseInt(o.stockBefore, 10))
                : null,
              ingredientId: g.optionType === "ingredientes" ? (o.ingredientId || null) : null,
              // Adicionais fracionados: ligado, o peso vale (countAs); desligado, fica guardado (countAsOff) e deixa de
              // valer — religar volta como era. 1 não grava.
              ...(o.countAs === 0.5 || o.countAs === 2 ? (fracionados ? { countAs: o.countAs } : { countAsOff: o.countAs }) : {}),
              ...(o.inPackage === false ? { inPackage: false } : {}),
            })),
        }));

      // Pacote de escolhas: ligado, os números têm de fechar (M ≥ N; máximo vazio = sem limite); desligado, guarda o que
      // estiver preenchido.
      const prepN = parseInt(prepIncluded, 10);
      const prepSemMax = prepMax.trim() === "";
      const prepM = parseInt(prepMax, 10);
      const prepE = parseFloat(String(prepExtra).replace(",", ".")) || 0;
      const prepNumerosOk = Number.isFinite(prepN) && prepN >= 0 && (prepSemMax || (Number.isFinite(prepM) && prepM >= prepN));
      if (prepEnabled && !prepNumerosOk) {
        toast({
          title: "Confira o pacote de escolhas",
          description: "Preencha quantos itens estão inclusos e, se tiver máximo, um número igual ou maior.",
          variant: "destructive",
        });
        return;
      }
      const prepCountPayload = prepNumerosOk
        ? {
            enabled: prepEnabled,
            included: prepN,
            max: prepSemMax ? null : prepM,
            extraPrice: Math.round(prepE * 100) / 100,
            unitName: prepUnit.trim() || null,
            ...(prepPodeMenos ? { required: false } : {}),
            ...(prepPorLista ? { porLista: true } : {}),
          }
        : null;

      // Preço-base 0 com adicional com valor é o «A partir de» (ex: marmita). Sem adicional com valor, o produto sai de
      // graça no cardápio: pede confirmação (Pedro, 27/10/2026: lojas precisam de produto a R$ 0, como «consultar
      // disponibilidade», e contornavam com um adicional obrigatório de R$ 0,01).
      const parsedPrice = parseFloat(formData.price.replace(',', '.'));
      const basePrice = Number.isFinite(parsedPrice) ? parsedPrice : 0;
      if (basePrice <= 0 && !hasPricedOptions(cleanedGroups as unknown as OptionGroup[]) && !(await confirm({
        title: "Salvar com preço R$ 0,00?",
        description: "No cardápio este produto vai aparecer por R$ 0,00, e o cliente poderá pedir sem pagar por ele.",
        confirmLabel: "Salvar mesmo assim",
      }))) return;
      // Promoção ligada exige desconto válido — senão o produto salvava sem
      // promoção em silêncio (o lojista achava que estava no ar e não estava).
      if (promoEnabled && promoKind === "simple") {
        const dv = parseFloat(promoDiscountValue.replace(',', '.'));
        if (Number.isNaN(dv) || dv <= 0) {
          toast({
            title: "Defina o desconto da promoção",
            description: promoDiscountType === "percent"
              ? "Informe a porcentagem de desconto ou desligue a Promoção."
              : "Informe o preço promocional ou desligue a Promoção.",
            variant: "destructive",
          });
          return;
        }
        if (promoDiscountType === "percent" && dv > 100) {
          toast({
            title: "Desconto inválido",
            description: "A porcentagem não pode passar de 100%.",
            variant: "destructive",
          });
          return;
        }
      }
      if (promoEnabled && promoKind === "quantity") {
        if (soldByWeight) {
          toast({
            title: "Promoção por quantidade não vale pra venda por peso",
            description: "Produto vendido por peso não tem \"unidades\". Use o desconto simples.",
            variant: "destructive",
          });
          return;
        }
        if (promoQtyMode === "progressive") {
          const step = parseFloat(promoQtyStepValue.replace(',', '.'));
          const cap = parseFloat(promoQtyMax.replace(',', '.'));
          if (Number.isNaN(step) || step <= 0 || (promoQtyStepType === "percent" && step > 100)) {
            toast({ title: "Defina o desconto por unidade", description: "Informe quanto cada unidade extra desconta (ou desligue a Promoção).", variant: "destructive" });
            return;
          }
          // Teto OBRIGATÓRIO: sem ele o desconto cresceria sem limite e o
          // servidor trata como "sem promoção" (fail-open) — salvaria no ar
          // sem estar.
          if (Number.isNaN(cap) || cap <= 0 || (promoQtyStepType === "percent" && cap > 100)) {
            toast({ title: "Defina o desconto máximo", description: "O progressivo precisa de um teto (ex.: até 50%).", variant: "destructive" });
            return;
          }
        } else {
          const dv = parseFloat(promoQtyDiscValue.replace(',', '.'));
          const gate = promoQtyMode === "threshold" || promoQtyMode === "all_from"
            ? parseInt(promoQtyFrom, 10)
            : parseInt(promoQtyN, 10);
          if (!Number.isFinite(gate) || gate < 2) {
            toast({
              title: promoQtyMode === "threshold" ? "A partir de qual unidade?" : promoQtyMode === "all_from" ? "Levando quantas unidades?" : "A cada quantas unidades?",
              description: "Informe um número a partir de 2.",
              variant: "destructive",
            });
            return;
          }
          if (Number.isNaN(dv) || dv <= 0 || (promoQtyDiscType === "percent" && dv > 100)) {
            toast({
              title: "Defina o desconto da promoção",
              description: promoQtyDiscType === "percent"
                ? "Informe a porcentagem de desconto (até 100)."
                : "Informe o preço promocional da unidade.",
              variant: "destructive",
            });
            return;
          }
        }
      }
      // Guardas da AGENDA (valem pra simples E por quantidade): sem elas a
      // promoção salvava "ligada" e nunca disparava — semanal sem dia marcado
      // e período sem data são o mesmo furo silencioso do desconto vazio, pela
      // outra porta. Decisão do Pedro (10/08): bloquear > default preenchido.
      if (promoEnabled && promoScheduleType === "weekly" && promoWeekDays.length === 0) {
        toast({
          title: "Escolha os dias da promoção",
          description: "Marque ao menos um dia da semana, ou mude para \"Sempre\".",
          variant: "destructive",
        });
        return;
      }
      if (promoEnabled && promoScheduleType === "range") {
        const startTs = promoStartsAt ? new Date(promoStartsAt).getTime() : NaN;
        const endTs = promoEndsAt ? new Date(promoEndsAt).getTime() : NaN;
        if (Number.isNaN(startTs) || Number.isNaN(endTs)) {
          toast({
            title: "Defina o período da promoção",
            description: "Informe a data de início e a de fim, ou mude para \"Sempre\".",
            variant: "destructive",
          });
          return;
        }
        if (startTs >= endTs) {
          toast({
            title: "Período inválido",
            description: "A data de fim precisa ser depois da data de início.",
            variant: "destructive",
          });
          return;
        }
      }

      const parsedOverride = parseFloat(startingPriceOverride.replace(',', '.'));
      const startingOverridePayload =
        Number.isFinite(parsedOverride) && parsedOverride > 0 ? parsedOverride : null;

      const payload: any = {
        name: formatNameForSave(formData.name),
        description: formData.description,
        price: basePrice,
        startingPriceOverride: startingOverridePayload,
        stock: noStockControl
          ? null
          : (Number.isFinite(parseInt(formData.stock, 10)) ? parseInt(formData.stock, 10) : 0),
        categoryId: formData.categoryId || undefined,
        status: formData.status,
        imageUrls: imageUrls,
        ...((): { video?: { id: string; posicao: number; capa: number } | null } => {
          // Ausente = não mexe: o último envio que falhou e ninguém tocou fica de fora (o cardápio segue como está).
          if (video && videoEnvio.envios[video.id]?.estado.status !== "falhou") {
            return { video: { id: video.id, posicao: Math.min(video.posicao, imageUrls.length), capa: video.capa } };
          }
          if (!video && videoInicial) return { video: null };
          return {};
        })(),
        servings: isFood && formData.servings ? parseInt(formData.servings, 10) : null,
        slices: isFood && parseInt(formData.slices, 10) > 0 ? parseInt(formData.slices, 10) : null,
        isVegan,
        isFood,
        barcode: limparCodigoProduto(barcode) || null,
        optionGroups: cleanedGroups && cleanedGroups.length > 0 ? cleanedGroups : null,
        removableIngredients: isFood && removableIngredients.length > 0 ? removableIngredients : null,
        isSuggestive,
        soldByWeight,
        portions: (() => {
          if (!soldByWeight || !modoPorcoes) return null;
          const out = portionRows.map((r) => {
            const kgBruto = parseFloat(r.kgStr.replace(",", "."));
            const kg = Math.round((emUnidades ? kgBruto / 1000 : kgBruto) * 1000) / 1000;
            const pr = parseFloat(r.priceStr.replace(",", "."));
            return { kg, price: Number.isFinite(pr) && pr > 0 ? Math.round(pr * 100) / 100 : null };
          }).filter((o) => Number.isFinite(o.kg) && o.kg >= 0.001 && o.kg <= 999);
          return out.length > 0 ? out : null;
        })(),
        weightPublic: soldByWeight && weightPublic,
        weightUnit,
        // O servidor NULLa o lado que não vale (exclusividade por flag); mandar
        // os dois explícitos deixa o intent claro no payload.
        stockKg: soldByWeight && !noStockControl && stockKgStr.trim() !== ""
          ? parseFloat(stockKgStr.replace(",", ".")) / (weightUnit === "ml" ? 1000 : 1)
          : null,
        costPrice: (parseFloat(costPrice.replace(",", ".")) || 0) > 0 ? parseFloat(costPrice.replace(",", ".")) : null,
        isOrderBased,
        minAdvanceDays: isOrderBased && advanceUnit === "dias" && antecedencia ? parseInt(antecedencia, 10) : null,
        minAdvanceHours: isOrderBased && advanceUnit === "horas" && parseInt(antecedencia, 10) > 0 ? parseInt(antecedencia, 10) : null,
        dailyCapacity: isOrderBased && dailyCapacity ? parseInt(dailyCapacity, 10) : null,
        orderFromDate: isOrderBased && orderFromDate ? orderFromDate : null,
        orderUntilDate: isOrderBased && orderUntilDate ? orderUntilDate : null,
        orderScheduleMode: isOrderBased ? scheduleMode : null,
        orderShifts: isOrderBased && scheduleMode === "turno" ? shifts : null,
        orderShiftCapacity: isOrderBased && scheduleMode === "turno" && parseInt(shiftCapacity, 10) > 0 ? parseInt(shiftCapacity, 10) : null,
        // Por encomenda ou interruptor desligado: null (sem faixa escondida que limite a venda sem a loja saber).
        saleHours: !isOrderBased && vendaComHorario ? horarioDeVendaParaSalvar(diasDeVenda) : null,
        maxPerCart: limitPerCart && parseInt(maxPerCart, 10) > 0 ? parseInt(maxPerCart, 10) : null,
        prepCount: prepCountPayload,
        availabilityConfig: buildAvailabilityConfig({
          isOrderBased,
          enabled: availabilityEnabled,
          mode: availabilityMode,
          weeklyDays,
          monthlyDays,
          monthlyRanges,
          specificDates,
          specificRanges,
        }),
      };

      const discValNum = parseFloat(promoDiscountValue.replace(',', '.'));
      const promoQtyValid = promoEnabled && promoKind === "quantity";
      if (promoQtyValid || (promoEnabled && !Number.isNaN(discValNum) && discValNum > 0)) {
        const promo: any = promoQtyValid
          ? {
              enabled: true,
              kind: "quantity",
              // Top-level percent/0 de propósito: bundle VELHO em cache lê a
              // promoção como simple — sem isso, discountValue ausente viraria
              // "fixed R$ 0" e zeraria o preço na tela do cliente até o reload.
              discountType: "percent",
              discountValue: 0,
              scheduleType: promoScheduleType,
              quantity:
                promoQtyMode === "progressive"
                  ? {
                      mode: "progressive",
                      step: {
                        discountType: promoQtyStepType,
                        discountValue: parseFloat(promoQtyStepValue.replace(',', '.')),
                      },
                      maxDiscount: parseFloat(promoQtyMax.replace(',', '.')),
                    }
                  : promoQtyMode === "every_nth"
                    ? {
                        mode: "every_nth",
                        n: parseInt(promoQtyN, 10),
                        discountType: promoQtyDiscType,
                        discountValue: parseFloat(promoQtyDiscValue.replace(',', '.')),
                      }
                    : {
                        mode: promoQtyMode === "all_from" ? "all_from" : "threshold",
                        fromUnit: parseInt(promoQtyFrom, 10),
                        discountType: promoQtyDiscType,
                        discountValue: parseFloat(promoQtyDiscValue.replace(',', '.')),
                      },
            }
          : {
              enabled: true,
              discountType: promoDiscountType,
              discountValue: discValNum,
              scheduleType: promoScheduleType,
            };
        if (promoChannels !== "both") promo.channels = promoChannels;
        if (promoScheduleType === "weekly") {
          promo.weekly = {
            days: promoWeekDays,
            hasTimeWindow: promoHasTimeWindow,
            startTime: promoHasTimeWindow ? promoStartTime || null : null,
            endTime: promoHasTimeWindow ? promoEndTime || null : null,
          };
        }
        if (promoScheduleType === "range") {
          promo.range = {
            startsAt: promoStartsAt ? new Date(promoStartsAt).toISOString() : null,
            endsAt: promoEndsAt ? new Date(promoEndsAt).toISOString() : null,
          };
        }
        payload.promotion = promo;
      } else {
        payload.promotion = null;
      }

      const templatesToCreate = optionGroups
        .filter(g => {
          if (!g.saveAsTemplate || !g.name.trim()) return false;
          return g.options.some(o => g.optionType === "ingredientes" ? !!o.ingredientId : !!o.name.trim());
        })
        .map(g => ({
          name: g.name.trim(),
          selectionType: g.selectionType,
          maxSelections: g.selectionType === "multiple" && g.maxSelections ? parseInt(g.maxSelections, 10) : null,
          minSelections: pisoDoGrupo(g) ?? null,
          required: g.required,
          onePerOption: g.selectionType === "multiple" ? !!g.onePerOption : false,
          priceMode: g.selectionType === "multiple" ? g.priceMode : "sum",
          optionType: g.optionType,
          options: g.options
            .filter(o => g.optionType === "ingredientes" ? !!o.ingredientId : !!o.name.trim())
            .map(o => ({
              name: o.name.trim(),
              price: parseFloat(o.price.replace(',', '.')) || 0,
              ingredientId: g.optionType === "ingredientes" ? (o.ingredientId || null) : null,
            })),
        }));

      // «Usar em todos» ligado: confirma ANTES de salvar (cancelar volta ao produto sem salvar nada) e, salvo o
      // produto, aplica o modo (com os turnos e a capacidade/turno) a todo produto por encomenda da loja.
      const nomeDoModo = scheduleMode === "turno" ? "Turno" : scheduleMode === "data" ? "Apenas o Dia" : "Dia e Hora";
      const aplicarEmTodos = isOrderBased && usarEmTodos;
      if (aplicarEmTodos && !(await confirm({
        title: `Usar «${nomeDoModo}» em todos os produtos por encomenda?`,
        description: "Vale para todos os produtos por encomenda da loja, já salvos. A antecedência, a capacidade e os dias de cada um continuam como estão.",
        confirmLabel: "Usar em todos",
      }))) return;

      if (product) {
        await updateMutation.mutateAsync({ id: product.id, data: payload });
      } else {
        await createMutation.mutateAsync({ data: payload });
      }

      if (aplicarEmTodos) {
        try {
          const r = await customFetch<{ updated: number }>("/api/products/order-schedule/all", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              orderScheduleMode: scheduleMode,
              orderShifts: scheduleMode === "turno" ? shifts : null,
              orderShiftCapacity: scheduleMode === "turno" && parseInt(shiftCapacity, 10) > 0 ? parseInt(shiftCapacity, 10) : null,
            }),
          });
          queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
          queryClient.invalidateQueries({ queryKey: ["settings"] });
          toast({ title: `Pronto: ${r.updated} ${r.updated === 1 ? "produto por encomenda" : "produtos por encomenda"} com «${nomeDoModo}».` });
        } catch (bulkErr) {
          toast({ title: "Produto salvo, mas não deu para usar em todos", description: extractApiErrorMessage(bulkErr, "Tente de novo."), variant: "destructive" });
        }
      }

      // Templates são criados APÓS o produto salvar com sucesso, evitando órfãos.
      // toast e queryClient continuam válidos mesmo após o modal fechar (vivem no contexto raiz).
      if (templatesToCreate.length > 0) {
        try {
          await Promise.all(
            templatesToCreate.map((t) =>
              customFetch("/api/option-group-templates", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(t),
              }),
            ),
          );
          queryClient.invalidateQueries({ queryKey: ["option-group-templates"] });
          toast({ title: templatesToCreate.length === 1 ? "Template salvo!" : `${templatesToCreate.length} templates salvos!` });
        } catch (tplErr: any) {
          toast({ title: "Produto salvo, mas falhou ao salvar template(s)", description: extractApiErrorMessage(tplErr), variant: "destructive" });
        }
      }
    } catch (e: any) {
      const msg = extractApiErrorMessage(e, "Não foi possível salvar o produto.");
      const wrapped = new Error(`[product-save] ${msg} | status=${e?.status ?? "?"} productId=${product?.id ?? "new"}`);
      reportError(wrapped, null);
      toast({ title: errorTitle("salvar o produto"), description: msg, variant: "destructive" });
    }
  };

  const isPending = uploadMutation.isPending || createMutation.isPending || updateMutation.isPending;

  // Atalho disparado pelo lápis no IngredientSelectField. Tenta salvar o produto
  // (preserva alterações em curso) e abre o popup de gestão de ingredientes.
  // Se o salvamento falhar, mantém o modal aberto pra usuário ver o erro.
  const openIngredientsManagerFromGroupEdit = async () => {
    try {
      await handleSubmit({ preventDefault: () => {} } as unknown as React.FormEvent);
    } catch {
      return;
    }
    onClose();
    onOpenIngredientsManager();
  };

  return (
    <ModalShell
      open={isOpen}
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={product ? "Editar Produto" : "Novo Produto"}
      subtitle="Preencha os dados — só o nome é obrigatório (o preço pode vir dos adicionais)."
      size="lg"
      bodyClassName="bg-muted/30"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="product-form" disabled={isPending || videoSubindo}>
            {(isPending || videoSubindo) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Salvar Produto
          </Button>
        </>
      }
    >
        <form id="product-form" onSubmit={handleSubmit} className="space-y-3.5">
          <SectionCard icon={<Package className="h-4 w-4" />} title="Informações básicas">
            <div className="flex gap-3.5 items-start">
              {/* Quadrado abre a galeria DIRETO (label + input sobreposto opacity-0,
                  padrao a prova de mobile). Antes abria um modal aninhado que nao
                  abria de forma confiavel no celular. Gerenciar/reordenar segue no
                  botao "Gerenciar fotos" abaixo. */}
              <label
                className={`w-[88px] h-[88px] bg-muted rounded-xl border-2 border-dashed border-border flex items-center justify-center overflow-hidden relative group shrink-0 hover:border-primary transition-colors cursor-pointer ${squareUploading ? "opacity-60 cursor-wait" : ""}`}
                aria-label="Adicionar foto do produto"
              >
                {video && (video.posicao === 0 || imageUrls.length === 0) ? (
                  <MiniaturaDoVideo envio={videoEnvio.envios[video.id]} capa={video.capa} selo="centro" />
                ) : imageUrls[0] ? (
                  <img src={imageUrls[0]} className="w-full h-full object-cover" alt="" />
                ) : squareUploading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                ) : (
                  <ImageIcon className="w-7 h-7 text-muted-foreground/50" />
                )}
                {itensDoEditor.length > 0 && (
                  <span className="absolute bottom-1 right-1 text-[10px] font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded shadow">
                    {itensDoEditor.length}/{MAX_PRODUCT_IMAGES}
                  </span>
                )}
                <span className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs font-semibold pointer-events-none">
                  Adicionar
                </span>
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-wait"
                  disabled={squareUploading}
                  data-testid="produto-foto-input"
                  onChange={(e) => {
                    const picked = Array.from(e.target.files || []);
                    e.target.value = "";
                    if (picked.length > 0) void handleSquareFiles(picked);
                  }}
                />
              </label>
              <div className="flex-1 min-w-0 space-y-2">
                <Label>Nome do produto</Label>
                <Input required value={formData.name} onChange={e => setFormData({...formData, name: capitalizeWords(e.target.value)})} />
                <button
                  type="button"
                  onClick={() => setPhotoModalOpen(true)}
                  className="flex items-center gap-2 px-3 py-1.5 border rounded-md hover:bg-muted transition-colors text-xs w-full text-left text-muted-foreground"
                >
                  <ImageIcon className="w-3.5 h-3.5" />
                  <span className="flex-1">
                    {itensDoEditor.length === 0 ? "Adicionar fotos" : `Gerenciar fotos (${itensDoEditor.length}/${MAX_PRODUCT_IMAGES})`}
                  </span>
                </button>
              </div>
            </div>

            {video && (
              <PainelDoVideo
                envio={videoEnvio.envios[video.id]}
                onRemover={() => { descartarSeNovo(video.id); setVideo(null); }}
              />
            )}
            {avisoVideoPlano && (
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border bg-card p-3 text-xs text-muted-foreground" data-testid="video-plano">
                <span>Vídeo no produto é do plano Profissional.</span>
                <UltraBadge plan="profissional" />
              </div>
            )}

            <PhotoManagerModal
              isOpen={photoModalOpen}
              onClose={() => setPhotoModalOpen(false)}
              initialItens={itensDoEditor}
              onSave={aplicarItens}
              envios={videoEnvio.envios}
              capaDoVideo={video?.capa ?? 0}
              onEscolherVideo={escolherVideo}
              onDescartarVideo={descartarSeNovo}
            />

            <div className="mt-3.5 space-y-2">
              <Label>Descrição <span className="font-normal text-muted-foreground">(opcional)</span></Label>
              <CampoDescricao value={formData.description} onChange={(v) => setFormData({ ...formData, description: v })} />
            </div>
            <div className="mt-3.5">
            {!ultra && (
              <div className="mb-1.5">
                <UltraBadge />
              </div>
            )}
            <BarcodeLookupField
              value={barcode}
              onChange={setBarcode}
              autoFocus={!product && desktopPointer && podeBarcode}
              disabled={isPending || !podeBarcode}
              excludeId={product?.id ?? null}
              imageSlots={MAX_PRODUCT_IMAGES - itensDoEditor.length}
              onEditDuplicate={(id) => onEditProduct?.(id)}
              onImportImage={async (url) => {
                const r = await customFetch<{ url: string }>("/api/upload/image-from-url", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ url }),
                });
                setImageUrls((prev) => (prev.length + (video ? 1 : 0) >= MAX_PRODUCT_IMAGES ? prev : [...prev, r.url]));
              }}
              onResult={(r) => {
                // Só preenche o que está vazio. Preço nunca; isFood nunca.
                const s = r.sugestao;
                const nomeSugerido = s?.nome || r.outrasLojas?.nome || "";
                const descSugerida = s?.descricao || "";
                const candidatas = [...(s?.categorias ?? []), ...(r.outrasLojas?.categoria ? [r.outrasLojas.categoria] : [])];
                const fold = (t: string) => t.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
                const casada = candidatas.map((c) => categories.find((cat) => fold(cat.name) === fold(c)))
                  .find((cat): cat is Category => !!cat);
                setFormData((f) => ({
                  ...f,
                  name: f.name.trim() ? f.name : nomeSugerido.slice(0, 100),
                  description: f.description.trim() ? f.description : descSugerida,
                  categoryId: f.categoryId || (casada ? casada.id : f.categoryId),
                }));
                if (!casada && candidatas[0] && !formData.categoryId) { setNewCatSilent(true); setShowNewCat(true); setNewCatName(candidatas[0].slice(0, 40)); }
              }}
            />
            </div>
          </SectionCard>

          <SectionCard icon={<Tag className="h-4 w-4" />} title="Preço e estoque">
            <div className="grid grid-cols-2 gap-3.5 items-start">
              <div className="space-y-2">
                <Label>{soldByWeight ? (weightUnit === "ml" ? "Preço por litro" : "Preço por kg") : "Preço"}</Label>
                <CurrencyInput value={formData.price} onChange={(v) => setFormData({...formData, price: v})} />
                <Label className="text-xs text-muted-foreground">{soldByWeight ? (weightUnit === "ml" ? "Custo por litro (opcional)" : "Custo por kg (opcional)") : "Custo (opcional)"}</Label>
                <CurrencyInput value={costPrice} onChange={(v) => setCostPrice(v)} placeholder="R$ 0,00" />
                <p className="text-[11px] text-muted-foreground leading-snug">Com custo preenchido, cada venda gera a despesa "Custo — Pedido #N" no Financeiro automaticamente.</p>
              </div>
              {!soldByWeight ? (
              <div className="space-y-2">
                <Label>Estoque</Label>
                <Input type="number" required={!noStockControl} disabled={noStockControl} value={noStockControl ? "" : formData.stock} onChange={e => setFormData({...formData, stock: e.target.value})} className={noStockControl ? "bg-muted text-muted-foreground" : ""} />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={noStockControl} onChange={e => { setNoStockControl(e.target.checked); if (e.target.checked) setFormData(f => ({...f, stock: ""})); }} className="accent-primary w-4 h-4" />
                  <span className="text-sm text-muted-foreground">Não controlar</span>
                </label>
              </div>
              ) : (
              <div className="space-y-2">
                <Label>{weightUnit === "ml" ? "Estoque (ml)" : "Estoque (kg)"}</Label>
                <Input
                  inputMode="decimal"
                  required={!noStockControl}
                  disabled={noStockControl}
                  value={noStockControl ? "" : stockKgStr}
                  onChange={e => setStockKgStr(e.target.value.replace(/[^\d.,]/g, ""))}
                  className={noStockControl ? "bg-muted text-muted-foreground" : ""}
                  data-testid="product-stock-kg"
                />
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={noStockControl} onChange={e => { setNoStockControl(e.target.checked); if (e.target.checked) setStockKgStr(""); }} className="accent-primary w-4 h-4" />
                  <span className="text-sm text-muted-foreground">Não controlar</span>
                </label>
              </div>
              )}
            </div>
            {(() => {
              const parsed = parseFloat(formData.price.replace(",", "."));
              const base = Number.isFinite(parsed) ? parsed : 0;
              // Só guia o "preço pelos adicionais" quando o preço-base está
              // zerado E já existe algum grupo de adicionais (evita ruído em
              // produto novo comum com o campo de preço ainda vazio).
              if (base > 0 || optionGroups.length === 0) return null;
              // Espelha a limpeza do handleSubmit pra prever o "A partir de".
              const cleaned = optionGroups
                .filter(g => g.options.some(o => (g.optionType === "ingredientes" ? !!o.ingredientId : !!o.name.trim())))
                .map(g => ({
                  required: g.required,
                  options: g.options
                    .filter(o => (g.optionType === "ingredientes" ? !!o.ingredientId : !!o.name.trim()))
                    .map(o => ({ price: parseFloat(o.price.replace(",", ".")) || 0 })),
                })) as unknown as OptionGroup[];
              const priced = hasPricedOptions(cleaned);
              const auto = computeStartingFromPrice(cleaned);
              const ov = parseFloat(startingPriceOverride.replace(",", "."));
              const overrideVal = Number.isFinite(ov) && ov > 0 ? ov : null;
              const display = overrideVal ?? auto ?? 0;
              return (
                <div className="mt-3.5 space-y-2.5 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3">
                  {priced ? (
                    <>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Preço calculado pelos <strong>adicionais</strong>. Na loja aparece como{" "}
                        <strong className="text-foreground">"A partir de {formatCurrency(display)}"</strong>.
                      </p>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Valor "A partir de" <span className="font-normal text-muted-foreground">(opcional)</span></Label>
                        <CurrencyInput
                          value={startingPriceOverride}
                          onChange={(v) => setStartingPriceOverride(v)}
                          placeholder={auto ? `Automático: ${formatCurrency(auto)}` : "R$ 0,00"}
                        />
                        <p className="text-[11px] text-muted-foreground leading-relaxed">Deixe em branco para usar automaticamente o adicional obrigatório mais barato.</p>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Para vender com preço calculado pelos adicionais (ex: marmita), deixe o preço zerado e adicione adicionais com valor na seção <strong className="text-foreground">"Adicionais no produto"</strong> abaixo.
                    </p>
                  )}
                </div>
              );
            })()}
          </SectionCard>

          <ToggleCard
            icon={<Weight className="h-[18px] w-[18px]" />}
            title="Venda por peso"
            subtitle="Preço por kg; o peso é informado no PDV"
            checked={soldByWeight}
            onCheckedChange={async (v) => {
              // Trocar o modo APAGA o estoque do outro lado no salvar (a
              // exclusividade é do servidor). Com saldo digitado, confirmar —
              // senão o lojista perde o número num clique de curiosidade.
              const perde = v
                ? (!noStockControl && formData.stock.trim() !== "" ? `${formData.stock} un` : null)
                : (!noStockControl && stockKgStr.trim() !== "" ? `${stockKgStr} ${weightUnit === "ml" ? "ml" : "kg"}` : null);
              if (perde && !(await confirm({
                title: "Trocar o modo apaga o estoque?",
                description: `Este produto tem ${perde} em estoque. Mudar o modo de venda apaga esse saldo ao salvar.`,
                confirmLabel: "Trocar mesmo assim",
              }))) return;
              setSoldByWeight(v);
              // Só apaga o saldo do OUTRO modo. ⚠️ Ligar peso não desliga o
              // controle de estoque: desde o `stock_kg` o controle existe em
              // kg, e marcar "Não controlar" sozinho fazia o lojista achar que
              // produto por peso não tem estoque.
              if (v) setFormData(f => ({ ...f, stock: "" }));
              else setStockKgStr("");
            }}
          >
            <p className="text-xs text-muted-foreground leading-relaxed">
              O campo <strong className="text-foreground">Preço</strong> acima passa a ser o <strong className="text-foreground">{weightUnit === "ml" ? "preço por litro" : "preço por kg"}</strong>. No PDV, o operador digita {weightUnit === "ml" ? "a quantidade" : "o peso da balança"} e o valor sai calculado. O estoque, se controlado, é em {weightUnit === "ml" ? "ml" : "kg"}.
            </p>
            <div className="mt-3 space-y-2">
              <Label>Unidade do peso</Label>
              <div className="grid grid-cols-3 gap-2">
                {/* Trocar a unidade RECONVERTE os pesos digitados (800 g ↔ 0,8):
                    sem isso, "800" digitado em gramas virava 800 QUILOS num
                    clique silencioso — aconteceu no teste do Pedro (02/09).
                    Entre g e ml (1 ml = 1 g) o número fica; o estoque, digitado
                    em ml só no ml, reconverte quando entra ou sai do ml. */}
                {(["kg", "g", "ml"] as const).map((u) => (
                  <Button key={u} type="button" variant={weightUnit === u ? "default" : "outline"} size="sm" onClick={() => {
                    if (u === weightUnit) return;
                    const antes = weightUnit !== "kg", depois = u !== "kg";
                    if (antes !== depois) {
                      setPortionRows(portionRows.map((r) => {
                        const b = parseFloat(r.kgStr.replace(",", "."));
                        if (!Number.isFinite(b)) return r;
                        return { ...r, kgStr: depois ? String(Math.round(b * 1000)) : String(b / 1000).replace(".", ",") };
                      }));
                    }
                    if ((weightUnit === "ml") !== (u === "ml") && stockKgStr.trim() !== "") {
                      const s = parseFloat(stockKgStr.replace(",", "."));
                      if (Number.isFinite(s)) setStockKgStr(u === "ml" ? String(Math.round(s * 1000)) : String(s / 1000).replace(".", ","));
                    }
                    setWeightUnit(u);
                  }} data-testid={`product-weight-unit-${u}`}>
                    {u === "kg" ? "Quilos (kg)" : u === "g" ? "Gramas (g)" : "Mililitros (ml)"}
                  </Button>
                ))}
              </div>
              <Label>Como o cliente escolhe</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={!modoPorcoes ? "default" : "outline"} size="sm" onClick={async () => {
                  // Mudar pra peso livre APAGA as porções no salvar — confirmar
                  // antes (mesma guarda da troca de modo de estoque). Foi assim
                  // que as 4 porções do Pedro sumiram caladas em 02/09.
                  const validas = portionRows.filter((r) => r.kgStr.trim() !== "").length;
                  if (modoPorcoes && validas > 0 && !(await confirm({
                    title: "Trocar pra peso livre?",
                    description: `As ${validas} porções deixam de existir ao salvar.`,
                    confirmLabel: "Trocar",
                  }))) return;
                  setModoPorcoes(false);
                }}>
                  Peso livre
                </Button>
                <Button type="button" variant={modoPorcoes ? "default" : "outline"} size="sm" onClick={() => setModoPorcoes(true)}>
                  Porções
                </Button>
              </div>
              {modoPorcoes && <div className="space-y-2">
                {portionRows.map((row, ri) => {
                  const kgBruto = parseFloat(row.kgStr.replace(",", "."));
                  const kg = Math.round((emUnidades ? kgBruto / 1000 : kgBruto) * 1000) / 1000;
                  const perKg = parseFloat(formData.price.replace(",", "."));
                  const sugestao = Number.isFinite(kg) && Number.isFinite(perKg)
                    ? String((Math.round(kg * perKg * 100) / 100).toFixed(2)).replace(".", ",")
                    : "";
                  return (
                    <div key={ri} className="flex items-center gap-2">
                      <Input
                        value={row.kgStr}
                        onChange={(e) => {
                          const v = e.target.value;
                          const b = parseFloat(v.replace(",", "."));
                          const kgNovo = Math.round((emUnidades ? b / 1000 : b) * 1000) / 1000;
                          const calc = Number.isFinite(kgNovo) && Number.isFinite(perKg)
                            ? String((Math.round(kgNovo * perKg * 100) / 100).toFixed(2)).replace(".", ",")
                            : "";
                          setPortionRows(portionRows.map((r, i) => i === ri
                            ? { ...r, kgStr: v, priceStr: r.priceTouched ? r.priceStr : calc }
                            : r));
                        }}
                        placeholder={weightUnit === "ml" ? "Quantidade (ml)" : weightUnit === "g" ? "Peso (g)" : "Peso (kg)"}
                        inputMode="decimal"
                        className="flex-1 min-w-0"
                      />
                      <Input
                        value={row.priceStr}
                        onChange={(e) => setPortionRows(portionRows.map((r, i) => i === ri ? { ...r, priceStr: e.target.value, priceTouched: true } : r))}
                        placeholder={sugestao ? `R$ ${sugestao}` : "Preço"}
                        inputMode="decimal"
                        className="flex-1 min-w-0"
                      />
                      <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10" aria-label="Remover porção"
                        onClick={() => setPortionRows(portionRows.filter((_, i) => i !== ri))}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
                <Button type="button" variant="outline" size="sm" disabled={portionRows.length >= 12}
                  onClick={() => setPortionRows([...portionRows, { kgStr: "", priceStr: "" }])}>
                  + Adicionar porção
                </Button>
                <p className="text-xs text-muted-foreground">O preço vem do peso; edite pra arredondar (ex.: R$ 64,00).</p>
              </div>}
              <label className="flex items-center justify-between gap-3 rounded-lg border border-border/60 px-3 py-2 cursor-pointer">
                <span className="text-sm">Vender no link público <span className="text-xs text-muted-foreground">— {modoPorcoes ? "cliente escolhe a porção" : "cliente digita o peso"}</span></span>
                <Switch checked={weightPublic} onCheckedChange={setWeightPublic} aria-label="Vender por peso no link" />
              </label>
            </div>
          </ToggleCard>

          <SectionCard icon={<Folder className="h-4 w-4" />} title="Organização">
            <Label>Categoria</Label>
            {showNewCat ? (
              <div className="flex items-center gap-2">
                <Input
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  placeholder="Nome da nova categoria"
                  className="flex-1 min-w-0 ring-2 ring-primary focus-visible:ring-primary"
                  autoFocus={!newCatSilent}
                  onKeyDown={(e) => { if (e.key === "Enter" && newCatName.trim()) { e.preventDefault(); createCatMutation.mutate({ data: { name: newCatName.trim() } }); } }}
                />
                <Button
                  type="button"
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-white shrink-0 h-10"
                  onClick={() => { if (newCatName.trim()) createCatMutation.mutate({ data: { name: newCatName.trim() } }); }}
                  disabled={!newCatName.trim() || createCatMutation.isPending}
                >
                  {createCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar"}
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="shrink-0 h-9 w-9"
                  onClick={() => { setShowNewCat(false); setNewCatName(""); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : editingCatId ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editCatName}
                  onChange={(e) => setEditCatName(e.target.value)}
                  placeholder="Nome da categoria"
                  className="flex-1 min-w-0 ring-2 ring-primary focus-visible:ring-primary"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter" && editCatName.trim()) { e.preventDefault(); updateCatMutation.mutate({ id: editingCatId, data: { name: editCatName.trim() } }); } }}
                />
                <Button
                  type="button"
                  className="bg-primary hover:bg-primary/90 text-white shrink-0 h-10"
                  onClick={() => { if (editCatName.trim()) updateCatMutation.mutate({ id: editingCatId, data: { name: editCatName.trim() } }); }}
                  disabled={!editCatName.trim() || updateCatMutation.isPending}
                >
                  {updateCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="shrink-0 h-10 w-10"
                  onClick={() => { setEditingCatId(null); setEditCatName(""); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <select
                  className="flex-1 min-w-0 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={formData.categoryId}
                  onChange={e => setFormData({...formData, categoryId: e.target.value})}
                >
                  <option value="">Selecionar categoria...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {formData.categoryId ? (
                  <>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="shrink-0 h-9 w-9 text-primary hover:text-primary hover:bg-primary/10"
                      onClick={() => {
                        const cat = categories.find(c => c.id === formData.categoryId);
                        if (cat) { setEditingCatId(cat.id); setEditCatName(cat.name); }
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="shrink-0 h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={async () => {
                        if (await confirm({
                          title: "Excluir esta categoria?",
                          description: "Os produtos dela ficarão sem categoria.",
                          confirmLabel: "Excluir",
                        })) {
                          deleteCatMutation.mutate({ id: formData.categoryId });
                        }
                      }}
                      disabled={deleteCatMutation.isPending}
                    >
                      {deleteCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="shrink-0 h-9 w-9"
                    onClick={() => { setNewCatSilent(false); setShowNewCat(true); }}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
          </SectionCard>

          <ToggleCard
            icon={<UtensilsCrossed className="h-[18px] w-[18px]" />}
            title="É alimento"
            subtitle="Habilita porções e ingredientes"
            checked={isFood}
            onCheckedChange={handleFoodToggle}
          >
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  Serve quantas pessoas? <span className="font-normal text-muted-foreground">(opcional)</span>
                </Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="Ex: 2"
                  value={formData.servings}
                  onChange={e => setFormData({...formData, servings: e.target.value})}
                  className="max-w-[160px]"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Pizza className="w-4 h-4 text-muted-foreground" />
                  Quantas fatias? <span className="font-normal text-muted-foreground">(opcional)</span>
                </Label>
                <Input
                  type="number"
                  min="1"
                  max="99"
                  placeholder="Ex: 8"
                  value={formData.slices}
                  onChange={e => setFormData({...formData, slices: e.target.value})}
                  className="max-w-[160px]"
                  data-testid="product-slices"
                />
              </div>
            </div>
          </ToggleCard>

          <ToggleCard
            icon={<Leaf className="h-[18px] w-[18px]" />}
            title="Vegano"
            subtitle="Selo no link público"
            checked={isVegan}
            onCheckedChange={setIsVegan}
          />

          <SectionCard icon={<ListChecks className="h-4 w-4" />} title="Adicionais no produto">
            <div className="space-y-2.5">
              {optionGroups.length === 0 && (
                <p className="text-xs text-muted-foreground">Nenhum adicional ainda. Crie um pra oferecer acompanhamentos, sabores, extras…</p>
              )}
              {/* Adicionais reordenam por setas no topo-direito do card (arrastar
                  o card grande não ficou bom). As opções dentro deles arrastam. A
                  ordem do array é a ordem no link público; persistida ao salvar. */}
              {optionGroups.map((group, gi) => (
                  <div key={group.id} className="rounded-xl border border-border bg-muted/40 p-3 space-y-3">
                    {optionGroups.length > 1 && (
                      <div className="flex justify-end gap-1 -mt-1 -mr-1">
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground" onClick={() => moveGroup(gi, -1)} disabled={gi === 0} title="Mover adicional para cima">
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground" onClick={() => moveGroup(gi, 1)} disabled={gi === optionGroups.length - 1} title="Mover adicional para baixo">
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label className="text-xs">Título dos adicionais</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Ex: Acompanhamentos"
                          value={group.name}
                          onChange={e => updateGroup(gi, "name", e.target.value)}
                          className="flex-1 min-w-0 h-9 text-sm font-semibold bg-card"
                        />
                        <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-destructive" onClick={() => removeGroup(gi)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Tipo de adicionais</Label>
                      <select
                        className="w-full h-9 rounded-md border border-input bg-card px-2.5 text-sm"
                        value={group.optionType}
                        onChange={e => updateGroup(gi, "optionType", e.target.value)}
                        title="Como as opções deste adicional são definidas."
                      >
                        <option value="ingredientes">Ingredientes</option>
                        <option value="personalizado">Personalizado</option>
                      </select>
                    </div>

                    <OptionSegmented
                      value={group.selectionType === "multiple" ? "multiple" : "single"}
                      onChange={(v) => updateGroup(gi, "selectionType", v)}
                    />

                    {/* Pacote de escolhas: o grupo não tem máximo próprio (quem limita é o Máximo do produto). */}
                    {group.selectionType === "multiple" && !prepEnabled && (
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-semibold text-muted-foreground">Máximo de seleções</span>
                        <MaxStepper value={group.maxSelections} onChange={(v) => updateGroup(gi, "maxSelections", v)} />
                      </div>
                    )}


                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium leading-tight">Obrigatório</div>
                        <div className="text-xs text-muted-foreground leading-snug">Cliente precisa escolher</div>
                      </div>
                      <Switch
                        className="mt-0.5 shrink-0"
                        checked={group.required}
                        onCheckedChange={(v) => {
                          // Desligar obrigatório apaga o piso: regra escondida
                          // continuaria valendo no servidor.
                          setOptionGroups(optionGroups.map((g, idx) => idx === gi ? { ...g, required: v, ...(v ? {} : { minSelections: "" }) } : g));
                        }}
                      />
                    </div>

                    {group.selectionType === "multiple" && group.required && (() => {
                      const min = parseInt(group.minSelections, 10);
                      const teto = parseInt(group.maxSelections, 10);
                      const temTeto = Number.isFinite(teto) && teto >= 1;
                      const piso = Number.isFinite(min) && min > 1 ? Math.min(min, temTeto ? teto : min) : 1;
                      const legenda =
                        piso <= 1
                          ? "Basta 1 escolha"
                          : temTeto && piso >= teto
                            ? `O cliente escolhe as ${teto}`
                            : `O cliente escolhe pelo menos ${piso}`;
                      return (
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium leading-tight">Mínimo de seleções</div>
                            <div className="text-xs text-muted-foreground leading-snug">{legenda}</div>
                          </div>
                          <MaxStepper value={group.minSelections} emptyLabel="1" onChange={(v) => updateGroup(gi, "minSelections", v)} />
                        </div>
                      );
                    })()}

                    {group.selectionType === "multiple" && (
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium leading-tight">Permitir apenas 1 de cada</div>
                          <div className="text-xs text-muted-foreground leading-snug">Cliente não repete a mesma opção</div>
                        </div>
                        <Switch className="mt-0.5 shrink-0" checked={group.onePerOption} onCheckedChange={(v) => updateGroup(gi, "onePerOption", v)} />
                      </div>
                    )}

                    {group.selectionType === "multiple" && (
                      <div className="space-y-1.5">
                        <Label className="text-xs">Preço das opções</Label>
                        <select
                          className="w-full h-9 rounded-md border border-input bg-card px-2.5 text-sm"
                          value={group.priceMode}
                          onChange={e => updateGroup(gi, "priceMode", e.target.value)}
                        >
                          <option value="sum">Somar o preço de cada opção</option>
                          <option value="highest">Cobrar pela opção mais cara</option>
                          <option value="average">Cobrar pela média das opções</option>
                        </select>
                      </div>
                    )}

                    <div className="space-y-2.5">
                      <SortableList
                        items={group.options}
                        onReorder={(newOpts) => reorderOptionsInGroup(gi, newOpts)}
                        className="space-y-2.5"
                        handleInside
                        renderItem={(opt, oi, handle) => (
                          // Card COMPLETO: cabeçalho com a alça e o remover
                          // dentro dele (antes a alça ficava solta ao lado e o
                          // conjunto parecia quebrado). Fundo do card mais
                          // claro que o painel = contraste de verdade.
                          <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                            <div className="flex items-center gap-2 border-b border-border/60 px-2 py-1.5">
                              <button
                                type="button"
                                ref={handle?.ref}
                                {...(handle?.attributes ?? {})}
                                {...(handle?.listeners ?? {})}
                                className="touch-none text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing shrink-0"
                                title="Arrastar para reordenar"
                              >
                                <GripVertical className="w-4 h-4" />
                              </button>
                              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Opção {oi + 1}</span>
                              <Button type="button" variant="ghost" size="icon" className="h-6 w-6 ml-auto shrink-0 text-destructive" onClick={() => removeOptionFromGroup(gi, oi)} disabled={group.options.length <= 1} title="Remover opção">
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                            <div className="p-2.5 space-y-2">
                            {group.optionType === "ingredientes" ? (
                              <>
                                <IngredientSelectField
                                  value={opt.name}
                                  ingredientId={opt.ingredientId}
                                  onChange={({ name, ingredientId }) => {
                                    setOptionGroups(optionGroups.map((g, idx) => idx === gi ? {
                                      ...g,
                                      options: g.options.map((o, oi2) => oi2 === oi ? { ...o, name, ingredientId } : o),
                                    } : g));
                                  }}
                                  onEditClick={() => openIngredientsManagerFromGroupEdit()}
                                />
                                <div className="space-y-1">
                                  <Label className="text-[11px] text-muted-foreground">Preço a mais</Label>
                                  <CurrencyInput
                                    value={opt.price}
                                    onChange={(v) => updateOptionInGroup(gi, oi, "price", v)}
                                    className="h-9 text-sm"
                                    placeholder="R$ 0,00"
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="space-y-1">
                                  <Label className="text-[11px] text-muted-foreground">Nome da opção</Label>
                                  <Input
                                    placeholder="Ex: Bacon"
                                    value={opt.name}
                                    onChange={e => updateOptionInGroup(gi, oi, "name", e.target.value)}
                                    className="h-9 text-sm"
                                  />
                                </div>
                                <div className="flex items-end gap-2">
                                  <div className="flex-[2] min-w-0 space-y-1">
                                    <Label className="text-[11px] text-muted-foreground">Preço a mais</Label>
                                    <CurrencyInput
                                      value={opt.price}
                                      onChange={(v) => updateOptionInGroup(gi, oi, "price", v)}
                                      className="h-9 text-sm"
                                      placeholder="R$ 0,00"
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0 space-y-1">
                                    <Label className="text-[11px] text-muted-foreground">Estoque</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      inputMode="numeric"
                                      placeholder="∞"
                                      title="Vazio = sem controle de estoque. 0 = esgotado."
                                      value={opt.stock}
                                      onChange={e => updateOptionInGroup(gi, oi, "stock", e.target.value)}
                                      className="h-9 text-sm"
                                    />
                                  </div>
                                </div>
                              </>
                            )}
                            {fracionados && (
                              <div className="flex items-center justify-between gap-3" data-testid={`option-weight-${gi}-${oi}`}>
                                <span className="text-[11px] font-semibold text-muted-foreground">Conta como</span>
                                <div className="flex gap-0.5 rounded-lg bg-muted p-0.5">
                                  {[0.5, 1, 2].map((w) => (
                                    <button
                                      key={w}
                                      type="button"
                                      onClick={() => setOptionWeight(gi, oi, w)}
                                      className={`min-w-[2.25rem] rounded-md px-2 py-1 text-xs font-semibold transition-colors ${(opt.countAs ?? 1) === w ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
                                    >
                                      {w === 0.5 ? "½" : w}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            </div>
                          </div>
                        )}
                      />
                      <Button type="button" variant="ghost" size="sm" className="text-xs h-9" onClick={() => addOptionToGroup(gi)}>
                        <Plus className="w-3 h-3 mr-1" /> Adicionar opção
                      </Button>
                    </div>

                    <label className="flex items-center justify-end gap-1.5 cursor-pointer border-t border-border/50 pt-2" title="Salva uma cópia deste adicional como modelo para reusar em outros produtos.">
                      <input
                        type="checkbox"
                        checked={!!group.saveAsTemplate}
                        onChange={e => updateGroup(gi, "saveAsTemplate", e.target.checked)}
                        className="accent-primary w-3.5 h-3.5"
                        data-testid={`option-group-save-as-template-${gi}`}
                      />
                      <span className="text-xs text-muted-foreground">Salvar como modelo pra reusar</span>
                    </label>
                  </div>
              ))}
            </div>
            <div className="mt-3">
              <OptionGroupTemplatesPicker
                onCreateBlank={addOptionGroup}
                onSelectTemplate={addOptionGroupFromTemplate}
              />
            </div>
            {/* Adicionais fracionados (Pedro, 15/10/2026; Profissional): aqui, junto das opções que ganham «Conta como». */}
            <div className="mt-3">
              <ToggleCard
                icon={<SlidersHorizontal className="h-[18px] w-[18px]" />}
                title="Adicionais fracionados"
                subtitle="Cada opção pode contar ½ ou 2 no limite do grupo"
                checked={fracionados}
                switchDisabled={!fracionados && !podePesos}
                onCheckedChange={(v) => setFracionados(v)}
                badge={!pagaPlano ? <UltraBadge plan="profissional" /> : undefined}
              >
                <p className="text-xs text-muted-foreground" data-testid="option-weight-hint">
                  Em cada opção acima, escolha quanto ela conta: ½, 1 ou 2. O «Escolha até» do grupo soma esses pesos.
                </p>
              </ToggleCard>
            </div>
          </SectionCard>

          {/* Pacote de escolhas logo abaixo dos adicionais (Pedro, 15/10/2026). Os dois recursos do Profissional têm os
              mesmos títulos da página de planos; ligar exige o plano (ou a liberação), desligar sempre pode. */}
          <ToggleCard
            icon={<ListChecks className="h-[18px] w-[18px]" />}
            title="Pacote de escolhas"
            subtitle="Limite total entre os grupos e extras pagos"
            checked={prepEnabled}
            switchDisabled={!prepEnabled && !podePreparos}
            onCheckedChange={(v) => setPrepEnabled(v)}
            badge={!pagaPlano ? <UltraBadge plan="profissional" /> : undefined}
          >
            <div className="space-y-3" data-testid="prep-count-fields">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Inclusos no preço</Label>
                  <Input type="number" min="0" placeholder="Ex: 7" value={prepIncluded} onChange={(e) => setPrepIncluded(e.target.value)} data-testid="prep-included" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Máximo</Label>
                  <Input type="number" min="0" placeholder="Sem limite" value={prepMax} onChange={(e) => setPrepMax(e.target.value)} data-testid="prep-max" />
                </div>
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-sm">Pode marcar menos</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Até {prepIncluded || "N"} no preço: quem marca menos paga o preço normal; o extra conta a partir de{" "}
                    {Number.isFinite(parseInt(prepIncluded, 10)) ? parseInt(prepIncluded, 10) + 1 : "N+1"}.
                  </p>
                </div>
                <Switch checked={prepPodeMenos} onCheckedChange={setPrepPodeMenos} className="mt-0.5 shrink-0" data-testid="prep-pode-menos" />
              </div>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Label className="text-sm">Cada lista conta separado</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Os inclusos, o extra e o máximo valem para cada lista que conta. Ex.: combo de 2 copos, cada copo com{" "}
                    {prepIncluded || "N"} no preço.
                  </p>
                </div>
                <Switch checked={prepPorLista} onCheckedChange={setPrepPorLista} className="mt-0.5 shrink-0" data-testid="prep-por-lista" />
              </div>
              {optionGroups.filter((g) => g.name.trim()).length > 1 && (
                <div className="space-y-1.5" data-testid="prep-listas">
                  <Label className="text-sm">Listas que contam</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {optionGroups.map((g, gi) =>
                      g.name.trim() ? (
                        <button
                          key={g.id}
                          type="button"
                          aria-pressed={g.inPackage !== false}
                          onClick={() => updateGroup(gi, "inPackage", g.inPackage === false ? undefined : false)}
                          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${g.inPackage !== false ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground"}`}
                          data-testid={`prep-lista-${gi}`}
                        >
                          {g.name.trim()}
                        </button>
                      ) : null,
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">A lista desmarcada fica fora do pacote e segue com o próprio limite.</p>
                </div>
              )}
              {(() => {
                // Só os adicionais COM PREÇO das listas que contam (e os já marcados fora): é o caso de quem cobra o
                // próprio preço sem ocupar vaga; todos os sem preço deixariam a lista longa demais (Personal Chef).
                const fichas = new Map<string, { fora: boolean }>();
                for (const g of optionGroups) {
                  if (g.inPackage === false || !g.name.trim()) continue;
                  for (const o of g.options) {
                    const nome = o.name.trim();
                    if (!nome || !((parseFloat(o.price.replace(",", ".")) || 0) > 0 || o.inPackage === false)) continue;
                    const f = fichas.get(nome) ?? { fora: true };
                    f.fora = f.fora && o.inPackage === false;
                    fichas.set(nome, f);
                  }
                }
                if (fichas.size === 0) return null;
                return (
                  <div className="space-y-1.5" data-testid="prep-adicionais">
                    <Label className="text-sm">Adicionais com preço que contam</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {Array.from(fichas.entries()).map(([nome, f]) => (
                        <button
                          key={nome}
                          type="button"
                          aria-pressed={!f.fora}
                          onClick={() => marcarAdicionalNoPacote(nome, f.fora)}
                          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${!f.fora ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground"}`}
                          data-testid={`prep-adicional-${nome}`}
                        >
                          {nome}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">O desmarcado cobra o preço dele e não ocupa vaga no pacote.</p>
                  </div>
                );
              })()}
              <div className="space-y-1.5">
                <Label className="text-sm">Preço de cada extra</Label>
                <CurrencyInput value={prepExtra} onChange={(v) => setPrepExtra(v)} placeholder="R$ 0,00" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Como chamar os itens</Label>
                <Input maxLength={20} placeholder="itens" value={prepUnit} onChange={(e) => setPrepUnit(e.target.value)} data-testid="prep-unit" />
                <p className="text-[11px] text-muted-foreground">No plural, como o cliente lê: «7 marmitas no pacote».</p>
              </div>
              <p className="text-xs text-muted-foreground">
                {prepPorLista
                  ? "Cada lista que conta tem a própria conta. Passando dos inclusos numa lista, cada item a mais nela custa o extra, até o máximo (se tiver)."
                  : "O total soma as opções marcadas nas listas que contam. Passando dos inclusos, cada item a mais custa o extra, até o máximo (se tiver)."}
              </p>
            </div>
          </ToggleCard>

          {/* Só pra alimento: o payload gateia removableIngredients por isFood,
              então mostrar o card em não-alimento perderia o que o lojista digita. */}
          {isFood && (
          <SectionCard icon={<MinusCircle className="h-4 w-4" />} title="Itens removíveis">
            <p className="mb-2.5 text-xs text-muted-foreground">O cliente pode pedir pra tirar estes itens.</p>
            {removableIngredients.length > 0 && (
              <div className="mb-2.5 flex flex-wrap gap-2">
                {removableIngredients.map((ing, i) => (
                  <span key={i} className="flex items-center gap-1 bg-muted px-2.5 py-1 rounded-full text-xs font-medium">
                    {ing}
                    <button type="button" onClick={() => setRemovableIngredients(removableIngredients.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                placeholder="Ex: Cebola"
                value={newIngredient}
                onChange={e => setNewIngredient(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addIngredient(); } }}
                className="flex-1 min-w-0 h-9 text-sm"
              />
              <Button type="button" variant="outline" size="sm" className="h-9 shrink-0" onClick={addIngredient} disabled={!newIngredient.trim()}>
                <Plus className="w-3 h-3 mr-1" /> Adicionar
              </Button>
            </div>
          </SectionCard>
          )}

          <ToggleCard
            icon={<CalendarDays className="h-[18px] w-[18px]" />}
            title="Por encomenda"
            subtitle={scheduleMode === "turno" ? "Exige agendar data e turno" : scheduleMode === "data" ? "Exige agendar a data" : "Exige agendar data e horário"}
            checked={isOrderBased}
            onCheckedChange={setIsOrderBased}
          >
            <div className="space-y-3">
              {/* <sm: um embaixo do outro — lado a lado, a coluna de ~150 px mostrava só o «E» do «Ex: 3» (Pedro, 19/10). */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Antecedência</Label>
                  <div className="flex gap-2">
                    <Input type="number" min={advanceUnit === "horas" ? "1" : "0"} placeholder={advanceUnit === "horas" ? "Ex: 24" : "Ex: 3"} value={antecedencia} onChange={e => setAntecedencia(e.target.value)} className="min-w-0" data-testid="product-advance-value" />
                    <select
                      value={advanceUnit}
                      onChange={e => setAdvanceUnit(e.target.value as "dias" | "horas")}
                      className="h-9 rounded-md border border-input bg-background px-2 text-sm shrink-0"
                      aria-label="Unidade da antecedência"
                      data-testid="product-advance-unit"
                    >
                      <option value="dias">dias</option>
                      <option value="horas">horas</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Capacidade/dia</Label>
                  <Input type="number" min="1" placeholder="Sem limite" value={dailyCapacity} onChange={e => setDailyCapacity(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1.5" data-testid="product-order-window">
                <Label className="text-sm">Aceitar encomendas</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">De</span>
                    <DataBr value={orderFromDate} onChange={setOrderFromDate} aria-invalid={janelaInvalida} data-testid="product-order-from" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs text-muted-foreground">Até</span>
                    <DataBr value={orderUntilDate} onChange={setOrderUntilDate} aria-invalid={janelaInvalida} data-testid="product-order-until" />
                  </div>
                </div>
                {janelaInvalida && (
                  <p className="text-xs text-destructive" data-testid="product-order-window-erro">A data de início fica depois da data final. Ajuste uma das duas.</p>
                )}
              </div>
              <div className="space-y-2 border-t border-border/30 pt-3" data-testid="encomenda-modo">
                <Label className="text-sm">O cliente escolhe</Label>
                <div className="flex gap-0.5 rounded-lg bg-muted p-0.5" role="radiogroup" aria-label="O cliente escolhe">
                  {([["horario", "Dia e Hora"], ["turno", "Turno"], ["data", "Apenas o Dia"]] as const).map(([v, rotulo]) => (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={scheduleMode === v}
                      onClick={() => setScheduleMode(v)}
                      className={`flex flex-1 items-center justify-center rounded-md py-2 text-xs font-semibold transition-colors ${scheduleMode === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}
                      data-testid={`encomenda-modo-${v}`}
                    >
                      {rotulo}
                    </button>
                  ))}
                </div>
                {scheduleMode === "turno" && (
                  <div className="space-y-2 pl-3 border-l-2 border-border/60">
                    {turnosDaLoja.map((t) => (
                      <div key={t} className="flex items-center justify-between gap-2">
                        <span className="text-sm">
                          {NOME_DO_TURNO[t]} <span className="text-xs text-muted-foreground">· {faixaDoTurnoTexto(horariosDosTurnos[t])}</span>
                        </span>
                        <Switch
                          checked={shifts.includes(t)}
                          disabled={shifts.filter((x) => turnosDaLoja.includes(x)).length === 1 && shifts.includes(t)}
                          onCheckedChange={(v) => setShifts(v ? TURNOS.filter((x) => x === t || shifts.includes(x)) : shifts.filter((x) => x !== t))}
                          data-testid={`encomenda-turno-${t}`}
                        />
                      </div>
                    ))}
                    <div className="flex items-center gap-2 pt-1">
                      <Label className="text-sm font-normal shrink-0">Capacidade/turno</Label>
                      <Input type="number" min="1" placeholder="Sem limite" value={shiftCapacity} onChange={(e) => setShiftCapacity(e.target.value)} className="max-w-[140px] h-9" data-testid="encomenda-capacidade-turno" />
                    </div>
                    <p className="text-xs text-muted-foreground">O horário de cada turno fica em Configurações → Produtos.</p>
                  </div>
                )}
                <div className="flex items-center justify-between gap-3 pt-1" data-testid="encomenda-usar-em-todos">
                  <Label htmlFor="encomenda-usar-em-todos-switch" className="flex items-center gap-2 text-sm">
                    <ListChecks className="w-4 h-4 text-muted-foreground" />
                    Usar em todos os produtos por encomenda
                  </Label>
                  <Switch id="encomenda-usar-em-todos-switch" checked={usarEmTodos} onCheckedChange={setUsarEmTodos} data-testid="encomenda-aplicar-todos" />
                </div>
              </div>
              <AvailabilitySection
                enabled={availabilityEnabled}
                onEnabledChange={setAvailabilityEnabled}
                mode={availabilityMode}
                onModeChange={setAvailabilityMode}
                weeklyDays={weeklyDays}
                onWeeklyDaysChange={setWeeklyDays}
                monthlyDays={monthlyDays}
                onMonthlyDaysChange={setMonthlyDays}
                monthlyRanges={monthlyRanges}
                onMonthlyRangesChange={setMonthlyRanges}
                specificDates={specificDates}
                onSpecificDatesChange={setSpecificDates}
                specificRanges={specificRanges}
                onSpecificRangesChange={setSpecificRanges}
                newSpecificDate={newSpecificDate}
                onNewSpecificDateChange={setNewSpecificDate}
              />
            </div>
          </ToggleCard>

          {!isOrderBased && (
            <div data-testid="product-sale-hours">
              <ToggleCard
                icon={<Clock className="h-[18px] w-[18px]" />}
                title="Horário de venda"
                subtitle="Só pode ser pedido em dias e horários escolhidos"
                checked={vendaComHorario}
                onCheckedChange={(v) => {
                  setVendaComHorario(v);
                  if (v && Object.keys(diasDeVenda).length === 0) setDiasDeVenda({});
                }}
              >
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    No link, o cliente só consegue pedir nos dias e horários marcados, no relógio da loja. Fora deles o produto aparece indisponível, com o próximo horário. No PDV e no «Novo pedido» do painel não há limite.
                  </p>
                  <EditorDeDiasDaSemana dias={diasDeVenda} onChange={setDiasDeVenda} />
                  {erroNoHorarioDeVenda && (
                    <p className="text-xs text-destructive" data-testid="product-sale-hours-erro">
                      {erroNoHorarioDeVenda}
                    </p>
                  )}
                </div>
              </ToggleCard>
            </div>
          )}

          <ToggleCard
            icon={<Package className="h-[18px] w-[18px]" />}
            title="Limite por carrinho"
            subtitle="Máximo deste produto por pedido"
            checked={limitPerCart}
            onCheckedChange={(v) => { setLimitPerCart(v); if (v && !maxPerCart) setMaxPerCart("1"); }}
          >
            <div className="space-y-2">
              <div className="space-y-1.5">
                <Label className="text-sm">Máximo por carrinho</Label>
                <Input type="number" min="1" placeholder="Ex: 1" value={maxPerCart} onChange={e => setMaxPerCart(e.target.value)} className="max-w-[180px]" />
              </div>
              <p className="text-xs text-muted-foreground">
                O cliente não pode adicionar mais que isso ao carrinho. No link público aparece "Quantidade máxima por entrega: {maxPerCart && parseInt(maxPerCart, 10) > 0 ? parseInt(maxPerCart, 10) : "X"}".
              </p>
            </div>
          </ToggleCard>

          <ToggleCard
            icon={<Sparkles className="h-[18px] w-[18px]" />}
            title="Venda sugestiva"
            subtitle="Sugerido a todos os clientes no carrinho"
            checked={isSuggestive}
            onCheckedChange={setIsSuggestive}
          />

          <ToggleCard
            icon={<BadgePercent className="h-[18px] w-[18px]" />}
            title="Promoção"
            subtitle="Desconto programado automático"
            checked={promoEnabled}
            onCheckedChange={(v) => { setPromoEnabled(v); if (v && !promoDiscountValue) setPromoDiscountValue("10"); }}
            accent
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm">Tipo de promoção</Label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={promoKind}
                  onChange={e => setPromoKind(e.target.value as "simple" | "quantity")}
                >
                  <option value="simple">Desconto no preço</option>
                  <option value="quantity">Por quantidade (leve mais, pague menos)</option>
                </select>
              </div>

              {promoKind === "simple" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Tipo de desconto</Label>
                  <select
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={promoDiscountType}
                    onChange={e => { const t = e.target.value as "percent" | "fixed"; setPromoDiscountType(t); if (t === "percent" && !promoDiscountValue) setPromoDiscountValue("10"); }}
                  >
                    <option value="percent">Porcentagem (%)</option>
                    <option value="fixed">Valor fixo (R$)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">{promoDiscountType === "percent" ? "Desconto" : "Preço promocional"}</Label>
                  {promoDiscountType === "percent" ? (
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={promoDiscountValue}
                        onChange={e => setPromoDiscountValue(e.target.value)}
                        placeholder="Ex: 10"
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                    </div>
                  ) : (
                    <CurrencyInput value={promoDiscountValue} onChange={(v) => setPromoDiscountValue(v)} />
                  )}
                </div>
              </div>
              )}

              {promoKind === "quantity" && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm">Regra</Label>
                    <select
                      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={promoQtyMode}
                      onChange={e => setPromoQtyMode(e.target.value as any)}
                    >
                      {/* "as seguintes" dava a entender que a Nª NÃO entrava —
                          a regra é: a Nª JÁ sai com desconto (combinado 09/08). */}
                      {/* Nomes do Pedro (10/10): "todas com desconto" dava a entender que a 1ª entrava (Chocojo) — a
                          regra que inclui a 1ª é a "Levando N ou mais". "As anteriores" e não "a 1ª": o rótulo é fixo e
                          com N = 4 são três unidades cheias. Sem "(inclusive a 1ª)" na outra: o TODAS já diz (Pedro, 10/10). */}
                      <option value="threshold">Da Nª unidade em diante com desconto (as anteriores ficam com preço cheio)</option>
                      <option value="all_from">Levando N ou mais, TODAS com desconto</option>
                      <option value="every_nth">A cada N unidades, uma sai com desconto (leve 3, pague 2)</option>
                      <option value="progressive">Desconto que cresce por unidade extra (com teto)</option>
                    </select>
                  </div>

                  {promoQtyMode !== "progressive" && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label className="text-sm">{promoQtyMode === "threshold" ? "A partir da" : promoQtyMode === "all_from" ? "Levando a partir de" : "A cada"}</Label>
                        <div className="relative">
                          <Input
                            type="number" min="2" step="1"
                            value={promoQtyMode === "threshold" || promoQtyMode === "all_from" ? promoQtyFrom : promoQtyN}
                            onChange={e => (promoQtyMode === "threshold" || promoQtyMode === "all_from" ? setPromoQtyFrom(e.target.value) : setPromoQtyN(e.target.value))}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">{promoQtyMode === "threshold" ? "ª un." : "un."}</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Desconto</Label>
                        <select
                          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={promoQtyDiscType}
                          onChange={e => setPromoQtyDiscType(e.target.value as any)}
                        >
                          <option value="percent">%</option>
                          <option value="fixed">Preço (R$)</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">{promoQtyDiscType === "percent" ? "Valor (%)" : "Preço da un."}</Label>
                        {promoQtyDiscType === "percent" ? (
                          <div className="relative">
                            <Input type="number" min="1" max="100" step="1" value={promoQtyDiscValue} onChange={e => setPromoQtyDiscValue(e.target.value)} placeholder="50" className="pr-8" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                          </div>
                        ) : (
                          <CurrencyInput value={promoQtyDiscValue} onChange={(v) => setPromoQtyDiscValue(v)} />
                        )}
                      </div>
                    </div>
                  )}

                  {promoQtyMode === "progressive" && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label className="text-sm">Tipo</Label>
                        <select
                          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={promoQtyStepType}
                          onChange={e => setPromoQtyStepType(e.target.value as any)}
                        >
                          <option value="percent">% por un.</option>
                          <option value="fixed">R$ por un.</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Por unidade extra</Label>
                        {promoQtyStepType === "percent" ? (
                          <div className="relative">
                            <Input type="number" min="1" max="100" step="1" value={promoQtyStepValue} onChange={e => setPromoQtyStepValue(e.target.value)} placeholder="10" className="pr-8" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                          </div>
                        ) : (
                          <CurrencyInput value={promoQtyStepValue} onChange={(v) => setPromoQtyStepValue(v)} />
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Desconto máximo *</Label>
                        {promoQtyStepType === "percent" ? (
                          <div className="relative">
                            <Input type="number" min="1" max="100" step="1" value={promoQtyMax} onChange={e => setPromoQtyMax(e.target.value)} placeholder="50" className="pr-8" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                          </div>
                        ) : (
                          <CurrencyInput value={promoQtyMax} onChange={(v) => setPromoQtyMax(v)} />
                        )}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2 border border-border/40">
                    {promoQtyMode === "threshold" && `Ex.: da ${promoQtyFrom || "2"}ª unidade em diante, cada uma sai ${promoQtyDiscType === "percent" ? `com ${promoQtyDiscValue || "?"}% de desconto` : `por ${promoQtyDiscValue ? `R$ ${promoQtyDiscValue}` : "?"}`} (a ${promoQtyFrom || "2"}ª já entra; ${(promoQtyFrom || "2") === "2" ? "a 1ª fica" : "as anteriores ficam"} com preço cheio). Vale somando as variações do mesmo produto.`}
                    {promoQtyMode === "all_from" && `Ex.: levando ${promoQtyFrom || "2"} ou mais, TODAS saem ${promoQtyDiscType === "percent" ? `com ${promoQtyDiscValue || "?"}% de desconto` : `por ${promoQtyDiscValue ? `R$ ${promoQtyDiscValue}` : "?"}`}, inclusive a 1ª; com menos de ${promoQtyFrom || "2"}, preço cheio. Vale somando as variações do mesmo produto.`}
                    {promoQtyMode === "every_nth" && `Ex.: a cada ${promoQtyN || "3"} unidades, a ${promoQtyN || "3"}ª sai ${promoQtyDiscType === "percent" ? (Number(promoQtyDiscValue) >= 100 ? "grátis (leve " + (promoQtyN || "3") + ", pague " + (Number(promoQtyN || "3") - 1) + ")" : `com ${promoQtyDiscValue || "?"}% de desconto`) : `por ${promoQtyDiscValue ? `R$ ${promoQtyDiscValue}` : "?"}`}.`}
                    {promoQtyMode === "progressive" && `Ex.: 2ª unidade com ${promoQtyStepValue || "?"}${promoQtyStepType === "percent" ? "%" : " R$"} de desconto, 3ª com o dobro, e assim por diante até o teto de ${promoQtyMax || "?"}${promoQtyStepType === "percent" ? "%" : " R$"}.`}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm">Onde vale</Label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={promoChannels}
                  onChange={e => setPromoChannels(e.target.value as any)}
                >
                  <option value="both">PDV e Link</option>
                  <option value="pdv">Somente PDV</option>
                  <option value="link">Somente Link</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Quando aplicar</Label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={promoScheduleType}
                  onChange={e => setPromoScheduleType(e.target.value as any)}
                >
                  <option value="always">Sempre</option>
                  <option value="weekly">Dias da semana</option>
                  <option value="range">Período (data início e fim)</option>
                </select>
              </div>

              {promoScheduleType === "weekly" && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm">Dias da semana</Label>
                    <div className="flex flex-wrap gap-2">
                      {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((label, idx) => {
                        const active = promoWeekDays.includes(idx);
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setPromoWeekDays(prev => prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx])}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${active ? "bg-accent text-accent-foreground border-accent" : "bg-background text-muted-foreground border-border hover:border-accent/50"}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Restringir por horário</Label>
                    <Switch checked={promoHasTimeWindow} onCheckedChange={setPromoHasTimeWindow} />
                  </div>
                  {promoHasTimeWindow && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Início</Label>
                        <Input type="time" value={promoStartTime} onChange={e => setPromoStartTime(e.target.value)} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Fim</Label>
                        <Input type="time" value={promoEndTime} onChange={e => setPromoEndTime(e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {promoScheduleType === "range" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Início</Label>
                    <DataBr tipo="datetime-local" value={promoStartsAt} onChange={setPromoStartsAt} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Fim</Label>
                    <DataBr tipo="datetime-local" value={promoEndsAt} onChange={setPromoEndsAt} />
                  </div>
                </div>
              )}

              {(() => {
                const base = parseFloat(formData.price.replace(',', '.'));
                const val = parseFloat(promoDiscountValue.replace(',', '.'));
                if (!base || isNaN(val) || val <= 0) return null;
                const final = promoDiscountType === "percent"
                  ? base * (1 - Math.min(val, 100) / 100)
                  : Math.min(base, val);
                return (
                  <p className="text-xs text-muted-foreground">
                    Preview: <span className="line-through">{formatCurrency(base)}</span>{" "}
                    <span className="font-semibold text-accent">{formatCurrency(final)}</span>
                  </p>
                );
              })()}
            </div>
          </ToggleCard>

        </form>
    </ModalShell>
  );
}
